/**
 * English dictionary.
 *
 * `satisfies Record<TranslationKey, string>` is the whole guarantee: a key that
 * exists in Arabic and not here is a **compile error**, and so is a key here that
 * does not exist in Arabic. v2 shipped seven server message keys that existed in
 * no dictionary at all — they always rendered as the generic banner.
 */
export const en = {
    // ---------- common ----------
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.delete": "Delete",
    "common.edit": "Edit",
    "common.year": "Year",
    "common.nameAr": "Arabic name",
    "common.nameEn": "English name",
    "common.add": "Add",
    "brand.credit": "Minwal by TWING",
    "common.close": "Close",
    "paging.shown": "Showing {shown} of {total}",
    "paging.more": "Show {n} more",
    "paging.invalid": "That page number isn't valid — refresh and try again",
    "common.back": "Back",
    "common.yes": "Yes",
    "common.no": "No",
    "plans.periodLabel": "Period",
    "common.retry": "Try again",
    "common.confirm": "Confirm",
    "common.search": "Search",
    "common.showPassword": "Show password",
    "common.hidePassword": "Hide password",
    // ---------- the four states ----------
    "state.crashed.title": "This screen crashed",
    "state.crashed.quote": "Quote the code and reference above — they lead straight to the fault.",
    "state.crashed.reload": "Reload the page",
    "state.loading.title": "Loading…",
    "state.error.title": "This screen could not load",
    "state.error.body": "Something went wrong on our side, not yours. Try again — and if it persists, send us the code and reference below.",
    "state.error.bodyRequest": "If you need help, send this code and reference.",
    "state.error.reference": "Reference",
    "state.error.code": "Error code",
    "state.forbidden.title": "You don't have this permission",
    "state.forbidden.body": "You can request it — it goes straight to your department head.",
    "state.forbidden.request": "Request permission",
    "state.forbidden.requested": "Request sent — you'll get an answer here",
    "state.empty.title": "Nothing here yet",
    "state.empty.body": "Anything you add will show up in this space.",
    // ---------- errors ----------
    "errors.internal": "An unexpected error occurred on our side. It has been recorded.",
    "errors.appUpdated": "A new version is out — refresh the page to continue.",
    "errors.validationFailed": "Some fields are missing or invalid — check the highlighted ones.",
    "errors.notFound": "What you're looking for doesn't exist, or was removed.",
    "errors.conflict": "Someone else changed this before you. Refresh and try again.",
    "errors.versionConflict": "You're working on an outdated version — refresh to see the latest.",
    "errors.rateLimited": "Too many attempts in a short time. Wait a moment and try again.",
    "errors.sessionInvalid": "Your session has ended. Please sign in again.",
    "errors.passwordChangeRequired": "You must change your password before continuing.",
    "errors.forbidden": "You don't have this permission.",
    "errors.scopeForbidden": "You hold this permission, but not over this particular record.",
    "errors.stepUpRequired": "This is a major operation — confirm it's you before continuing.",
    "errors.idempotencyKeyMissing": "The request arrived without its duplicate-protection key.",
    "errors.idempotencyKeyReused": "The same key was sent with different data — this is an incomplete retry.",
    "errors.storageQuotaExceeded": "The client's limit is {quotaGb} GB, {usedGb} is used, and this file is {incomingMb} MB. Delete files or raise the limit.",
    "errors.fileTypeNotAllowed": "That file type is not allowed: {contentType}",
    "errors.fileTooLarge": "The file is {sizeMb} MB; our limit is {limitMb} MB.",
    "errors.fileScanPending": "The file is still being scanned — wait a few seconds and try again.",
    "errors.fileInfected": "The scan found malware in this file ({signature}) — it was rejected and deleted.",
    "errors.overrideTooLong": "An override lasts at most {maxDays, plural, one {# day} other {# days}}; you asked for {requestedDays}.",
    "overrides.windowInvalid": "The override dates are not valid.",
    "overrides.startsInPast": "An override cannot start in the past.",
    "overrides.endsBeforeStart": "The end date must be after the start date.",
    "overrides.reasonRequired": "Write why this override is needed — so it can be reviewed later.",
    "roles.created": "Role created.",
    "roles.renamed": "Role name saved.",
    "roles.matrixSaved": "Capability matrix saved.",
    "roles.deleted": "Role deleted.",
    "roles.assigned": "Role assigned.",
    "overrides.created": "Override recorded with its end date.",
    "overrides.removed": "Override removed.",
    "roles.matrixRequired": "The matrix is missing.",
    "roles.capabilityUnknown": "The matrix contains an unknown capability.",
    "roles.duplicateRow": "The same capability appears twice with the same scope.",
    "roles.departmentScopeEmpty": "Pick the department — a department scope with none means nothing.",
    "roles.explicitScopeNotOnRole": "An explicit-record scope is given to a person, not a role.",
    "roles.nameRequired": "A role must have a name.",
    "people.created": "Employee added, and notified about their account.",
    "people.saved": "Employee details saved.",
    "people.deactivated": "Employee deactivated; their open work moved to their department head.",
    "people.reactivated": "Employee is active again.",
    "people.deleted": "Employee deleted — their name remains in the log.",
    "people.passwordReset": "Password reset, and they were notified.",
    "people.sessionsRevoked": "All their sessions were ended.",
    "people.notAnEmployee": "That is a client contact, not an employee — it cannot be chosen for internal work.",
    "people.nameRequired": "An employee's name needs at least two characters.",
    "people.emailInvalid": "That email is not valid.",
    "people.departmentRequired": "Pick at least one department.",
    "people.tooManyDepartments": "A person belongs to at most eight departments.",
    "people.nothingToChange": "Nothing changed.",
    "org.departmentCreated": "Department created.",
    "org.create": "New department",
    "org.nameAr": "Department name (Arabic)",
    "org.nameEn": "Department name (English)",
    "org.departmentSaved": "Department saved.",
    "org.departmentArchived": "Department archived.",
    "org.delegated": "Delegation recorded, and both were notified.",
    "org.delegationRevoked": "Delegation revoked, and the delegate's sessions were ended.",
    "org.nameRequired": "A department name needs at least two characters.",
    "org.nothingToChange": "Nothing changed.",
    "org.headMustBeActive": "A department head must be an active employee.",
    "org.headMustBeMember": "A department head must belong to that department.",
    "org.delegateIsHead": "They already head this department — no delegation needed.",
    "org.delegateMustBeActive": "A delegate must be an active employee.",
    "org.delegateMustBeMember": "A delegate must belong to the department.",
    "org.delegationReasonRequired": "Write why this delegation is needed — so it can be reviewed later.",
    "org.delegationWindowInvalid": "The delegation dates are not valid.",
    "org.delegationStartsInPast": "A delegation cannot start in the past.",
    "org.delegationEndsBeforeStart": "The end date must be after the start date.",
    "org.delegationTooLong": "A delegation lasts at most ninety days.",
    "access.reason": "Why do you need it?",
    "access.reasonHint": "Write at least one line explaining why — this is what is read when deciding.",
    "access.requested": "Your request was sent to your department head.",
    "access.withdrawn": "Request withdrawn.",
    "access.resolved": "Decision recorded, and the requester was notified.",
    "access.capabilityUnknown": "That capability does not exist.",
    "access.scopeNotAllowed": "That scope is not allowed for this capability.",
    "access.departmentScopeEmpty": "Pick the department.",
    "access.rejectionNeedsReason": "Write why you are rejecting it — so the requester understands.",
    "people.title": "People",
    "people.add": "Add employee",
    "people.filter": "Filter",
    "people.all": "All",
    "people.active": "Active",
    "people.inactive": "Deactivated",
    "people.stopped": "Deactivated",
    "people.noRole": "No role",
    "people.detail": "Employee profile",
    "people.email": "Email",
    "people.departments": "Departments",
    "people.overrides": "Permission exceptions",
    "people.noOverrides": "No overrides.",
    "people.granted": "Granted",
    "people.name": "Name",
    "people.viewAsPick": "Pick an employee other than yourself.",
    "people.viewAsInactive": "This employee is deactivated and cannot be viewed as.",
    "people.jobTitle": "Job title",
    "people.jobTitleHint": "Shown with the name. It grants nothing: permissions come from the role.",
    "people.editDetails": "Edit name and departments",
    "people.firstDepartmentPrimary": "The first department you pick is their primary one.",
    "people.addOverride": "Exception",
    "people.overrideCapability": "Permission",
    "people.overridePick": "Pick a permission…",
    "people.overrideEffect": "Grant or revoke",
    "people.overrideScope": "Over which scope",
    "people.overrideReason": "Reason — it is recorded and the account holder sees it",
    "people.overrideExpires": "Ends on",
    "people.removeOverride": "Revoke",
    "people.confirmRemoveOverride": "Withdraw this exception now? The permission goes back to what their role says.",
    "people.delete": "Delete employee",
    "people.deleteConfirm": "“{name}” will be deleted — sign-in closes, and the name stays in the log and on past work.",
    "roles.delete": "Delete role",
    "roles.deleteConfirm": "The role “{name}” will be deleted with its permissions.",
    "roles.deleteBlocked": "{n, plural, one {# employee holds} other {# employees hold}} this role — move them to another role first to delete it.",
    "org.rename": "Rename department",
    "org.archive": "Archive department",
    "org.archiveConfirm": "The department “{name}” will be archived for good — it leaves every list and permission.",
    "org.archiveBlocked": "{n, plural, one {# employee is} other {# employees are}} in this department — move them first to archive it.",
    "org.changeHead": "Head of department",
    "org.changeHeadAction": "Change the head",
    "people.revoked": "Denied",
    "people.expired": "Expired",
    "people.stop": "Suspend",
    "people.restore": "Bring back",
    "people.resetPassword": "Reset password",
    "people.endSessions": "Sign out of every device · {n}",
    "people.confirmStop": "Deactivate this employee? Their open work moves to their department head, and their sessions end.",
    "people.confirmReset": "Reset their password? They will be notified, and their sessions will end.",
    "people.nameField": "Employee name",
    "people.emptyTitle": "No people here",
    "people.emptyBody": "Employees appear here once you add them.",
    "nav.people": "People",
    "roles.title": "Roles",
    "roles.holders": "{n, plural, one {# employee} other {# employees}}",
    "roles.contactHolders": "{n, plural, one {# contact} other {# contacts}}",
    "roles.capabilityCount": "{n, plural, one {# capability} other {# capabilities}}",
    "roles.portal": "Portal role",
    "roles.create": "New role",
    "roles.saveName": "Save name",
    "roles.approveMatrix": "Approve matrix",
    "roles.chosen": "{n} of {of}",
    "roles.module.plans": "Plans",
    "roles.module.tasks": "Tasks",
    "roles.module.cards": "Cards",
    "roles.module.boards": "Boards",
    "roles.module.clients": "Clients",
    "roles.module.chat": "Chat",
    "roles.module.meetings": "Meetings",
    "roles.module.reports": "Reports",
    "roles.module.analytics": "Analytics",
    "roles.module.moderation": "Publishing",
    "roles.module.portal": "Client portal",
    "roles.module.notifications": "Notifications & actions",
    "roles.module.identity": "People",
    "roles.module.roles": "Roles & permissions",
    "roles.module.organization": "Departments",
    "roles.module.access_requests": "Access requests",
    "roles.module.platform": "System",
    "roles.module.other": "Other",
    "roles.moduleHint.plans": "Monthly plans, their items, tasks and client decisions",
    "roles.moduleHint.tasks": "Internal tasks, their comments and files",
    "roles.moduleHint.cards": "Day-to-day work on cards and their due dates",
    "roles.moduleHint.boards": "Boards and their templates",
    "roles.moduleHint.clients": "Client details, team and storage",
    "roles.moduleHint.chat": "Channels and messages",
    "roles.moduleHint.meetings": "Client meeting requests and decisions",
    "roles.moduleHint.reports": "Monthly client reports and their review",
    "roles.moduleHint.analytics": "Numbers, rankings and company goals",
    "roles.moduleHint.moderation": "The publishing queue, its slots and proof",
    "roles.moduleHint.portal": "What a client contact can do",
    "roles.moduleHint.notifications": "Your notifications and pending actions",
    "roles.moduleHint.identity": "Staff profiles, accounts and their security",
    "roles.moduleHint.roles": "Roles, their matrices and individual exceptions",
    "roles.moduleHint.organization": "Departments, their heads and delegation",
    "roles.moduleHint.access_requests": "Requesting access and deciding on it",
    "roles.moduleHint.platform": "System health, its log and settings",
    "roles.moduleHint.other": "Permissions outside any group",
    "roles.lead": "Choose what this role can do, and its reach: the whole company, their department, or only their clients. The change reaches everyone on it as soon as you approve it.",
    "roles.chosenOf": "{n} of {of} permissions",
    "roles.rename": "Rename",
    "roles.more": "Role options",
    "roles.pending": "{n, plural, one {# unapproved change} other {# unapproved changes}} — will reach {holders, plural, one {# person} other {# people}}",
    "roles.noPending": "No changes waiting for approval.",
    "roles.discard": "Discard changes",
    "roles.pick": "Pick a role from the list to see its permissions.",
    "roles.cannotGrant": "You cannot grant this",
    "roles.scopeFor": "Scope for {capability}",
    "scope.company": "Across the company",
    "scope.headed": "Departments they head",
    "scope.member": "Their departments",
    "scope.assignee": "What they are assigned",
    "scope.creator": "What they created",
    "scope.own": "Their own record",
    "scope.client": "Their assigned clients",
    "nav.roles": "Roles",
    "org.title": "Departments",
    "org.detail": "Department",
    "org.head": "Head",
    "org.noHead": "No head",
    "org.members": "Members",
    "org.memberCount": "{n, plural, one {# member} other {# members}}",
    "org.delegations": "Delegations",
    "org.noDelegations": "No delegations.",
    "org.delegationActive": "Active",
    "org.delegationScheduled": "Scheduled",
    "org.delegationEnded": "Ended",
    "org.delegate": "Delegate a stand-in",
    "org.delegateTo": "Delegate",
    "org.revoke": "Revoke",
    "org.until": "Until {when}",
    "org.reason": "Reason",
    "org.confirmRevoke": "Revoke this delegation? The delegate's sessions will end.",
    "org.countLine": "{n, plural, one {# department} other {# departments}}",
    "org.delegatedLine": "{n} with the head's authority delegated",
    "org.holdingUntil": "is standing in until {when}",
    "org.revokeDelegation": "Cancel delegation",
    "org.more": "Options for {name}",
    "org.note": "Delegation gives the stand-in the head's permissions on this department only, until the date you set. Archiving is locked while the department has members — move them first.",
    "org.open": "Open {name}",
    "nav.departments": "Departments",
    "access.title": "Access requests",
    "access.subtitle": "When someone presses something outside their permissions, they request it here — and you decide.",
    "access.tabs": "Tabs",
    "access.queue": "Awaiting me",
    "access.mine": "My requests",
    "access.pending": "Awaiting a decision",
    "access.approved": "Approved",
    "access.rejected": "Rejected",
    "access.withdrawnStatus": "Withdrawn",
    "access.withdraw": "Withdraw my request",
    "access.approve": "Approve",
    "access.reject": "Reject",
    "access.note": "Your reply (they will see it)",
    "access.decisionBy": "{name}'s decision",
    "access.noneMineTitle": "You have not requested anything",
    "access.noneMineBody": "When an action is refused, you will find a request button on that screen.",
    "access.noneQueueTitle": "No requests waiting",
    "access.noneQueueBody": "Requests from your team appear here.",
    "access.queueCount": "Awaiting me · {n}",
    "access.mineCount": "My requests · {n}",
    "access.mineWhen": "My request · {when}",
    "access.wantsLead": "Asks for:",
    "access.scopeOneDepartment": "on one named department",
    "access.scopeDepartments": "on {n, plural, one {# named department} other {# named departments}}",
    "access.reasonQuote": "“{reason}”",
    "access.notePlaceholder": "e.g. Approved until your colleague is back",
    "nav.access": "Access requests",
    "errors.backupUnusable": "The backup destination “{bucket}” is not working: {detail}",
    "setup.backupVerified": "The destination works — we wrote a file, read it, and deleted it.",
    "setup.done": "The system is set up. You can sign in now.",
    "setup.required": "This field is required.",
    "setup.step.identity": "System identity",
    "setup.step.owner": "Owner account",
    "setup.step.departments": "Departments",
    "setup.step.backup": "Backup",
    "setup.stepOf": "Step {n} of 4",
    "setup.livePreview": "Live preview — changes as you type",
    "setup.changeLater": "Everything here can be changed later in settings — except the first owner account.",
    "setup.namePlaceholder": "Your system's name",
    "setup.identityTitle": "Your system, your name and colour",
    "setup.identityBody": "This is what your team and clients see on every screen.",
    "setup.systemName": "System name",
    "setup.accent": "Brand colour",
    "setup.locale": "Main language",
    "setup.timezone": "Time zone",
    "setup.ownerTitle": "Owner account",
    "setup.ownerBody": "The first account in the system, holding every permission. Add your team afterwards from the people screen.",
    "setup.ownerName": "Name",
    "setup.passwordRule": "12 characters or more — a long sentence beats a complicated word.",
    "setup.departmentsTitle": "Agency departments",
    "setup.departmentsBody": "Departments decide who sees what and who gets work. We put in suggestions — edit or remove them.",
    "setup.departmentAr": "Department {n} Arabic name",
    "setup.departmentEn": "Department {n} English name",
    "setup.departmentRemove": "Remove department {n}",
    "setup.departmentAdd": "+ Add department",
    "setup.suggest.content.ar": "المحتوى",
    "setup.suggest.content.en": "Content",
    "setup.suggest.management.ar": "الإدارة",
    "setup.suggest.management.en": "Management",
    "setup.suggest.moderation.ar": "المودريشن",
    "setup.suggest.moderation.en": "Moderation",
    "setup.suggest.media.ar": "الميديا باينج",
    "setup.suggest.media.en": "Media Buying",
    "setup.backupTitle": "Where backups go",
    "setup.backupBody": "Every night a full copy of the system is saved here. We test the destination now — write a test file, read it, delete it — before continuing.",
    "setup.backupEndpoint": "Storage address",
    "setup.backupBucket": "Bucket name",
    "setup.backupAccessKey": "Access key",
    "setup.backupSecretKey": "Secret key",
    "setup.backupTest": "Test the destination",
    "setup.backupTesting": "Testing…",
    "setup.backupMustTest": "It has to be tested before “Finish” works.",
    "setup.next": "Next",
    "setup.finish": "Finish setup",
    "setup.doneTitle": "“{name}” is ready",
    "setup.doneOwner": "The owner account was created with every permission.",
    "setup.doneDepartments": "{n, plural, one {# department was} other {# departments were}} created.",
    "setup.doneBackup": "Backups start tonight.",
    "setup.doneNext": "Next: sign in, then add your clients and team.",
    "setup.signIn": "Sign in as the owner",
    "setup.emailInvalid": "That email is not valid.",
    "setup.departmentRequired": "At least one department is required.",
    "stepUp.title": "Confirm it is you",
    "stepUp.why": "This is a major operation, so the system asks for your password again. Not a fault.",
    "stepUp.thenRetries": "Once confirmed, the operation runs by itself.",
    "stepUp.thenPressAgain": "Once confirmed, press the button again.",
    "stepUp.password": "Your password",
    "stepUp.confirm": "Confirm",
    "home.title": "Home",
    "home.subtitle": "What is waiting for you right now.",
    "home.allClear": "Nothing is waiting for you",
    "home.allClearBody": "You are all caught up. Anything new shows here first.",
    "home.open": "Open it",
    "home.greetingMorning": "Good morning, {name}",
    "home.greetingEvening": "Good evening, {name}",
    "home.waitingLead": "{n, plural, one {# thing is} other {# things are}} waiting for you — start with the oldest.",
    "home.waitingTally": "waiting for you",
    "home.otherActions": "{n} more in your actions →",
    "home.openAll": "Open all {n}",
    "home.andMore": "and {n} more waiting",
    "home.dueChange": "{from} → {to}",
    "home.clearIn": "Nothing waiting in:",
    "home.activity": "What happened today",
    "home.live": "Live",
    "home.activityNone": "Nothing has happened yet today",
    "home.activityNoneBody": "As soon as someone works on one of your clients it shows up here, and each line opens its place.",
    "home.system": "The system",
    "home.myOverdue": "Work past its date",
    "home.myOverdueBody": "{n, plural, one {# item assigned to you is past its date} other {# items assigned to you are past their dates}}.",
    "home.myOverdueClear": "Nothing of yours is past its date.",
    "home.myOpenWork": "Your open work",
    "home.myOpenWorkBody": "{n, plural, one {# item assigned to you is still open} other {# items assigned to you are still open}}",
    "home.myOpenWorkClear": "You have no open work.",
    "home.awaitingReview": "Waiting for your review",
    "home.awaitingReviewBody": "{n, plural, one {# delivered task is} other {# delivered tasks are}} waiting on internal review.",
    "home.awaitingReviewClear": "No task is waiting for review.",
    "actions.kind.planTaskReview": "A delivered task awaits your review",
    "home.revisionExceptions": "Exceptions waiting on you",
    "home.revisionExceptionsBody": "{n, plural, one {# revision exception has} other {# revision exceptions have}} had no decision.",
    "home.revisionExceptionsClear": "No exception is waiting.",
    "home.dueChanges": "Date change requests",
    "home.dueChangesBody": "{n, plural, one {# date change request is} other {# date change requests are}} waiting on you.",
    "home.dueChangesClear": "No date change request is waiting.",
    "home.accessRequests": "Permission requests",
    "home.accessRequestsBody": "{n, plural, one {# permission request is} other {# permission requests are}} waiting on you.",
    "home.accessRequestsClear": "No permission request is waiting.",
    "home.storageAlerts": "Clients near their storage limit",
    "home.storageAlertsBody": "{n, plural, one {# client has} other {# clients have}} passed the warning threshold.",
    "home.storageAlertsClear": "No client is near the storage limit.",
    "settings.intro": "Every number and policy in the system lives here — a change applies once you press Save in its section.",
    "settings.noneTitle": "No settings",
    "settings.noneBody": "The system declares no settings — that is not normal, contact support.",
    "settings.groupIdentity": "System identity",
    "settings.groupWork": "Work and reminders",
    "settings.groupPlans": "Plans and revisions",
    "settings.groupStorage": "Storage and limits",
    "settings.groupSecurity": "Security",
    "settings.groupReports": "Reports",
    "settings.groupRetention": "Retention and backups",
    "settings.isDefault": "Default value — never changed",
    "settings.changedBy": "Changed by {name} — {when}",
    "settings.noEffectYet": "No effect yet",
    "settings.valueRequired": "A value is required.",
    "settings.valueInvalid": "That value is not accepted for this setting.",
    "setup.settingInvalid": "That value is not acceptable for this setting.",
    "notify.access.created.title": "A new access request",
    "notify.access.created.body": "An employee requested a capability — open it to decide.",
    "notify.access.resolved.title": "Your request was decided",
    "notify.access.resolved.body": "Your access request was decided — open it to see the outcome.",
    "notify.org.headChanged.title": "The department head changed",
    "notify.org.headChanged.body": "A new head was set for the department — capabilities changed with it.",
    "notify.org.delegationGranted.title": "You were delegated a head's authority",
    "notify.org.delegationGranted.body": "You now hold the department head's authority for a set period — open the department to see it.",
    "notify.org.delegationRevoked.title": "The delegation was revoked",
    "notify.org.delegationRevoked.body": "The delegation you held over the department was revoked.",
    "notify.identity.userCreated.title": "An account was created for you",
    "notify.identity.userCreated.body": "An account was created for you — change your password when you first sign in.",
    "notify.identity.userDeactivated.title": "Work moved to you",
    "notify.identity.userDeactivated.body": "Someone in your department was deactivated and their open work is now yours — redistribute it.",
    "notify.identity.userReactivated.title": "Your account is back",
    "notify.identity.userReactivated.body": "Your account is active again — you can sign in.",
    "notify.identity.passwordReset.title": "Your password was reset",
    "notify.identity.passwordReset.body": "An administrator reset your password. If you did not ask for this, tell administration now.",
    "notify.reports.submitted.title": "A report is waiting for your review",
    "notify.reports.submitted.body": "“{title}” was submitted and is waiting for you.",
    "notify.reports.reviewed.title": "Your report was reviewed",
    "notify.reports.reviewed.body": "“{title}”: {outcome}",
    "notify.reports.published.title": "A new report for you",
    "notify.reports.published.body": "“{title}” is available — {outcome}",
    "notify.reports.exceptionRequested.title": "Report exception requested",
    "notify.reports.exceptionRequested.body": "Someone asked for a second report this month: {reason}",
    "notify.reports.exceptionDecided.title": "The exception request was decided",
    "notify.reports.exceptionDecided.body": "{decision}",
    "notify.moderation.published.title": "The post is live",
    "notify.moderation.published.body": "{title} was published on {platform}.",
    "notify.moderation.unpublished.title": "A publication mark was withdrawn",
    "notify.moderation.unpublished.body": "{title} was marked published and the mark was withdrawn: {reason}",
    "notify.moderation.takedownRequested.title": "Removal requested for a live post",
    "notify.moderation.takedownRequested.body": "The client asked for {title} to come down from {platform} — a person has to remove it by hand.",
    "notify.moderation.takedownResolved.title": "The takedown request is closed",
    "notify.moderation.takedownResolved.body": "{title}: {resolution}",
    "notify.identity.sessionsRevoked.title": "Your sessions were ended",
    "notify.identity.sessionsRevoked.body": "All your sessions were ended — you will need to sign in again.",
    "errors.reportMonthTaken": "There is already a report for this client this month.",
    "errors.reportSelfReview": "This is your own report — nobody reviews what they wrote.",
    "errors.reportLocked": "This report is not in a state that allows editing ({state}).",
    "errors.reportNotInReview": "This report is not waiting for your review ({state}).",
    "errors.reportVersionPending": "A new version is already waiting for review.",
    "errors.reportExceptionAlreadyOpen": "There is already an open exception request for this month.",
    "errors.reportExceptionDecided": "That request was already decided.",
    "errors.publicationAlreadyPublished": "This post is already marked as published.",
    "errors.publicationNotPublished": "This post is not published yet, so there is no mark to withdraw.",
    "errors.publicationCancelled": "This row was cancelled — the item was most likely edited after it was approved.",
    "errors.takedownAlreadyOpen": "There is already an open takedown request for this post.",
    "errors.takedownAlreadyResolved": "That request was already closed.",
    "errors.destructiveInProduction": "This command erases data and this is a production installation — it was refused before touching a row.",
    "errors.envNotConfigured": "The runtime environment is not configured: {names}. Startup was refused on purpose — it will not run half-set-up.",
    "errors.mailNotConfigured": "Email is not enabled on this system. Contact your administrator.",
    "errors.mailSendFailed": "We could not send the message right now. Try again, and if it keeps failing give your administrator this code.",
    "errors.dependencyUnavailable": "A service the system depends on is down. Try again shortly.",
    "errors.requestAborted": "The request was cancelled before it finished. If the screen stays blank, refresh it.",
    // ---------- auth ----------
    // ---------- Reports ----------
    "chat.title": "Chat",
    "chat.channel.GENERAL": "Everyone",
    "chat.channel.CLIENT_INTERNAL": "Internal",
    "chat.channel.CLIENT_PORTAL": "With the client",
    "chat.channel.DIRECT": "Direct",
    "chat.channel.GROUP": "Group",
    "chat.newGroup": "New group",
    "chat.newDirect": "Message a colleague",
    "chat.groupName": "Group name",
    "chat.groupMembers": "Who is in it",
    "chat.groupCreated": "Group created.",
    "chat.groupNameRequired": "Give the group a name.",
    "chat.groupMembersRequired": "Pick at least one person besides yourself.",
    "chat.directStarted": "The conversation is open.",
    "chat.compose": "Write a message",
    "chat.mentionWho": "Mention who",
    "chat.mentionDepartment": "Mention a department",
    "chat.send": "Send",
    "chat.sent": "Sent.",
    "chat.edited": "edited",
    "chat.editedSaved": "Your edit is saved.",
    "chat.deletedByAuthor": "message removed",
    "chat.deletedByModerator": "message removed by a moderator",
    "chat.deleteMessage": "Delete message",
    "chat.messageDeleted": "Deleted — its line stays.",
    "chat.bodyRequired": "Write something first.",
    "chat.addMember": "Add someone",
    "chat.memberAdded": "Added to the group.",
    "chat.leave": "Leave the group",
    "chat.left": "You left the group.",
    "chat.foldedNote": "Folded — the next message reopens it",
    "chat.noMessagesYet": "Nothing written here yet",
    "chat.sentFile": "sent a file",
    "chat.lastLine": "{who}: {said}",
    "chat.you": "You",
    "chat.foldWarning": "This channel folds tomorrow unless someone writes",
    "chat.unread": "new",
    "chat.presence.online": "Online",
    "chat.presence.away": "Away",
    "chat.presence.offline": "Offline",
    "chat.noneTitle": "No channels",
    "chat.noneBody": "Start a conversation or create a group and you will find it here.",
    "chat.searchChannels": "Find a client or colleague…",
    "chat.section.general": "Everyone",
    "chat.section.clients": "Clients",
    "chat.section.groups": "Groups",
    "chat.section.direct": "Direct messages",
    "chat.pickChannel": "Pick a channel from the list to read it.",
    "chat.portalBand": "The client sees every message here — internal talk goes in the client's internal channel.",
    "chat.membersCount": "{count} in the channel",
    "chat.members": "Channel members",
    "chat.channelMore": "More on this channel",
    "chat.messageMore": "More on this message",
    "chat.reply": "Reply",
    "chat.replyingTo": "Replying to {name}:",
    "chat.cancelReply": "Cancel reply",
    "chat.quoteDeleted": "Message deleted",
    "chat.addReaction": "Add a reaction",
    "chat.reactionMine": "{emoji} {count} — including you, press to remove",
    "chat.reactionOthers": "{emoji} {count} — press to add",
    "chat.emoji": "Emoji",
    "chat.mention": "Mention a colleague or department",
    "emoji.search": "Search emoji",
    "emoji.searchHint": "Search in Arabic or English… “heart”, “laugh”",
    "emoji.groups": "Emoji groups",
    "emoji.recent": "Frequently used",
    "emoji.loading": "Loading emoji…",
    "emoji.loadFailed": "The emoji library didn't load — try again.",
    "emoji.noMatch": "No emoji by that name.",
    "emoji.group.smileys": "Smileys & emotion",
    "emoji.group.people": "People & body",
    "emoji.group.animals": "Animals & nature",
    "emoji.group.food": "Food & drink",
    "emoji.group.travel": "Travel & places",
    "emoji.group.activities": "Activities",
    "emoji.group.objects": "Objects",
    "emoji.group.symbols": "Symbols",
    "emoji.group.flags": "Flags",
    "time.today": "Today",
    "time.yesterday": "Yesterday",
    "time.tomorrow": "Tomorrow",
    "time.dayWithDate": "{word} · {date}",
    "time.durationPair": "{a} {b}",
    "time.minute.one": "1 minute",
    "time.minute.two": "2 minutes",
    "time.minute.few": "{n} minutes",
    "time.minute.other": "{n} minutes",
    "time.hour.one": "1 hour",
    "time.hour.two": "2 hours",
    "time.hour.few": "{n} hours",
    "time.hour.other": "{n} hours",
    "time.day.one": "1 day",
    "time.day.two": "2 days",
    "time.day.few": "{n} days",
    "time.day.other": "{n} days",
    "time.zone.cairo": "Cairo",
    "time.zone.riyadh": "Riyadh",
    "time.zone.dubai": "Dubai",
    "time.zone.kuwait": "Kuwait",
    "time.zone.doha": "Doha",
    "time.zone.amman": "Amman",
    "time.zone.london": "London",
    "time.zone.utc": "UTC",
    "common.blankValue": "…",
    "platforms.instagram": "Instagram",
    "platforms.facebook": "Facebook",
    "platforms.tiktok": "TikTok",
    "platforms.linkedin": "LinkedIn",
    "platforms.x": "X",
    "platforms.youtube": "YouTube",
    "platforms.snapchat": "Snapchat",
    "platforms.threads": "Threads",
    "colors.teal": "Teal",
    "colors.blue": "Blue",
    "colors.purple": "Purple",
    "colors.brown": "Brown",
    "colors.raspberry": "Raspberry",
    "colors.green": "Green",
    "colors.custom": "Custom colour",
    "platform.notifications.cadenceSetting": "Sent automatically, timed by the “{setting}” setting",
    "settings.noEffectYetHint": "It is saved, but nothing uses it yet — it takes effect in a coming update.",
    "cards.checklistProgress": "{done} of {total}",
    "plans.platforms": "Platforms",
    "plans.showToClient": "Hidden from the client — show it",
    "plans.hideFromClient": "The client sees it — hide it",
    "boards.pickClient": "Client",
    "boards.pickClientFirst": "Pick the client first",
    "home.allActivity": "All activity",
    "chat.emptyChannelTitle": "Nothing here yet",
    "chat.emptyChannelBody": "No messages in this channel — write the first one.",
    "errors.chatChannelNotReachable": "That channel does not reach you.",
    "errors.chatMessageNotYours": "That is not your message — deleting it is for moderators.",
    "errors.chatMessageGone": "That message has already been removed.",
    "errors.chatReactionInvalid": "A reaction must be a single emoji — pick one from the picker.",
    "errors.chatReplyTargetInvalid": "The message you're replying to was deleted or isn't in this channel.",
    "errors.chatDirectWithSelf": "You cannot open a conversation with yourself.",
    "errors.chatPortalChannelOnly": "That reply is for a client's channel only.",
    "errors.meetingOwnProposal": "You cannot accept your own proposal — the other side does that.",
    "errors.meetingLinkRequired": "The meeting link (Zoom or Meet) is needed with the time.",
    "errors.meetingStateInvalid": "The meeting is {state} — that action does not apply.",
    "errors.meetingNotOver": "Its time has not passed yet — wait until it does.",
    "notify.chat.mentioned.title": "Someone mentioned you in chat",
    "notify.chat.mentioned.body": "\u201c{channel}\u201d \u2014 {excerpt}",
    "notify.chat.portalMessage.title": "A client wrote in chat",
    "notify.chat.portalMessage.body": "\u201c{client}\u201d \u2014 {excerpt}",
    "notify.chat.portalReply.title": "A reply from the agency",
    "notify.chat.portalReply.body": "{excerpt}",
    "notify.chat.foldSoon.title": "A channel is about to fold",
    "notify.chat.foldSoon.body": "“{channel}” has been quiet for {days, plural, one {# day} other {# days}} — the next message brings it back.",
    "notify.meeting.requested.title": "A new meeting request",
    "notify.meeting.requested.body": "{client} asked for a meeting — {when}. Accept it or propose another time.",
    "notify.meeting.proposed.title": "A time proposed for your meeting",
    "notify.meeting.proposed.body": "Your team proposed {when} — accept it or offer another. The link appears once confirmed.",
    "notify.meeting.confirmed.title": "The meeting is confirmed",
    "notify.meeting.confirmed.body": "It is set for {when} — the link is on the meeting.",
    "notify.meeting.rejected.title": "The time was declined",
    "notify.meeting.rejected.body": "Reason: {reason}. Propose another time if the meeting is still needed.",
    "notify.meeting.cancelled.title": "The meeting was cancelled",
    "notify.meeting.cancelled.body": "It was {when} — reason: {reason}.",
    "notify.meeting.reminderHour.title": "Your meeting is in an hour",
    "notify.meeting.reminderHour.body": "{when} — get what you need ready before it.",
    "notify.meeting.reminderSoon.title": "Your meeting starts in 5 minutes",
    "notify.meeting.reminderSoon.body": "{when} — open the link and join.",
    "portal.chat.subtitle": "Your conversation with the agency \u2014 all in one place.",
    "portal.chat.compose": "Write to the agency",
    "portal.chat.sent": "Your message is delivered.",
    "portal.chat.noneTitle": "Nothing yet",
    "portal.chat.noneBody": "Write the first message and your team will answer you here.",
    "analytics.title": "Analytics",
    "analytics.subtitle": "Real numbers — click any one and it opens the list it was counted from.",
    "analytics.level.company": "Company",
    "analytics.level.department": "My department",
    "analytics.level.client": "Client",
    "analytics.level.mine": "My numbers",
    "analytics.period.this-month": "This month",
    "analytics.period.last-month": "Last month",
    "analytics.period.this-quarter": "This quarter",
    "analytics.period.year": "This year",
    "analytics.metric.planItemsDue": "Due in the period",
    "analytics.metric.planItemsCompleted": "Completed in the period",
    "analytics.metric.completionRate": "On-time rate",
    "analytics.metric.planItemsOverdue": "Ran late",
    "analytics.metric.planItemsOpen": "Still open",
    "analytics.metric.publishedOnTime": "Published on time",
    "analytics.metric.publishedLate": "Published late",
    "analytics.metric.avgResponseMinutes": "Average reply to a mention",
    "analytics.noChange": "unchanged",
    "analytics.drillTitle": "The list it was counted from",
    "analytics.drillEmptyTitle": "No rows",
    "analytics.drillEmptyBody": "The number is zero for this period — there is nothing to show.",
    "analytics.noneTitle": "No numbers for this period",
    "analytics.noneBody": "Nothing was due in the range you picked. Change the range, or wait for the first plan.",
    "analytics.ranking": "Ranking",
    "analytics.rankingMe": "This is you",
    "analytics.clients": "Client health",
    "analytics.workload": "Workload",
    "analytics.goals": "Targets",
    "analytics.goalNew": "+ New target",
    "analytics.goalMetric": "Metric",
    "analytics.goalTarget": "Target value",
    "analytics.goalDirection": "Direction",
    "analytics.goalDirection.at-least": "at least",
    "analytics.goalDirection.at-most": "at most",
    "analytics.goalPeriod": "Month",
    "analytics.goalState.achieved": "Achieved",
    "analytics.goalState.breached": "Breached",
    "analytics.goalState.pending": "Being measured",
    "analytics.goalSaved": "Target saved.",
    "analytics.goalDeleted": "Target deleted.",
    "analytics.goalTargetRequired": "Enter the target value.",
    "analytics.goalsNoneTitle": "No targets",
    "analytics.goalsNoneBody": "Set a target for a metric and a month, and we will tell you the moment it is met or missed.",
    "analytics.export": "Export CSV",
    "analytics.exported": "The file is ready.",
    "analytics.exportPick": "Open a number first — the file is exported from the list it was counted from.",
    "analytics.levelLabel": "Whose numbers",
    "analytics.periodLabel": "Period",
    "analytics.drillGo": "The list it was counted from →",
    "analytics.noPrevious": "No earlier period to compare with",
    "analytics.deltaUp": "▲ {n} vs the previous period",
    "analytics.deltaDown": "▼ {n} vs the previous period",
    "analytics.deltaUpPoints": "▲ {n, plural, one {# point} other {# points}} vs the previous period",
    "analytics.deltaDownPoints": "▼ {n, plural, one {# point} other {# points}} vs the previous period",
    "analytics.goalProgress": "{value} of {target}",
    "analytics.goalDelete": "Delete the target",
    "analytics.goalDeleteMark": "×",
    "errors.analyticsLevelNotAllowed": "“{metric}” has no meaning at the {level} level — pick a different one.",
    "errors.analyticsGoalDuplicate": "There is already a target for this metric in this month.",
    "notify.analytics.goalAchieved.title": "A target was met",
    "notify.analytics.goalAchieved.body": "“{metric}” reached {value} — the target was {target}.",
    "notify.analytics.goalBreached.title": "A target was missed",
    "notify.analytics.goalBreached.body": "“{metric}” ended at {value} — the target was {target}.",
    "reports.title": "Reports",
    "reports.subtitle": "One report per client per month — it passes the department head, then management, before it reaches the client.",
    "reports.new": "+ New report",
    "reports.titleField": "Report title",
    "reports.titleRequired": "Write a title for the report.",
    "reports.bodyField": "Body",
    "reports.bodyRequired": "Write the report body — one line is not a report.",
    "reports.month": "Month",
    "reports.submit": "Submit for review",
    "reports.submitted": "The report is submitted for review.",
    "reports.saved": "Saved.",
    "reports.newVersion": "We made a new version — the client still sees the old one until this is approved.",
    "reports.return": "Send it back",
    "reports.returnReasonRequired": "Write why — whoever reads it needs to know what to fix.",
    "reports.approvedHead": "Approved by the department head.",
    "reports.approvedAdmin": "Published to the client.",
    "reports.returned": "Sent back to its author.",
    "reports.state.DRAFT": "Draft",
    "reports.state.SUBMITTED": "With the head",
    "reports.state.HEAD_APPROVED": "With management",
    "reports.state.ADMIN_APPROVED": "Approved",
    "reports.state.PUBLISHED": "With the client",
    "reports.state.RETURNED": "Sent back",
    "reports.state.EXPIRED": "Replaced by a newer version",
    "reports.exception": "Request an exception",
    "reports.exceptionRow": "Exception request",
    "reports.exceptionReason": "Why a second report this month?",
    "reports.exceptionReasonRequired": "Write the reason.",
    "reports.exceptionAsked": "The request reached admin.",
    "reports.decisionRequired": "Write your decision and why.",
    "reports.grantException": "Allow a second report",
    "reports.refuseException": "Refuse the request",
    "reports.exceptionDecided": "Your decision is recorded.",
    "reports.pickClient": "Pick a client",
    "reports.save": "Save",
    "reports.edit": "Edit",
    "reports.decision": "Your decision and why",
    "reports.exceptionState.OPEN": "Waiting on management",
    "reports.exceptionState.GRANTED": "A second report is allowed",
    "reports.exceptionState.REFUSED": "Refused",
    "errors.reportEditAlreadyAsked": "There is already an open edit request on this report.",
    "reports.editAsked": "Your request reached the report’s author.",
    "reports.editReasonRequired": "Say what needs correcting.",
    "reports.requestEdit": "Request an edit",
    "reports.editReason": "What needs correcting?",
    "reports.clientAsked": "The client asked for an edit",
    "notify.reports.editRequested.title": "A client asked for an edit to a report",
    "notify.reports.editRequested.body": "“{title}” — {reason}",
    "reports.noneTitle": "No reports",
    "reports.noneBody": "As soon as someone writes a report for a client, it appears here with its review stage.",
    "reports.waiting": "Waiting on you",
    "reports.writeTitle": "New report",
    "reports.exceptionWants": "wants a second report this month: “{reason}”",
    "reports.decisionHint": "e.g. Approved — the campaign has its own budget",
    "reports.yourReview": "Waiting for your review",
    "reports.writtenBy": "Written by {name}",
    "reports.headApprovedBy": "Approved by {name} (department head)",
    "reports.fileCount": "{count, plural, one {# file} other {# files}}",
    "reports.openReview": "Open and review",
    "reports.editReport": "Edit the report",
    "reports.allClients": "All clients",
    "reports.clientColumn": "Client",
    "reports.thisMonth": "{month} · this month",
    "reports.inReview": "In review",
    "reports.missing": "Missing",
    "reports.missingWrite": "Missing — write it",
    "reports.matrixHint": "Press any cell to open the report. A dashed cell = nobody has written it yet — it opens “New report” for that client and month.",
    "reports.lastEdited": "Last edited {when}",
    "reports.versionTag": "New version",
    "reports.older": "Older months",
    "reports.newer": "Newer months",
    "reports.menu": "More — edit · request an exception",
    "reports.path": "Report path",
    "reports.step.written": "Written",
    "reports.step.head": "Department head",
    "reports.step.admin": "Management",
    "reports.step.client": "Client",
    "reports.stepApproved": "{name} approved",
    "reports.yourCall": "Waiting on you",
    "reports.stepWaiting": "Waiting",
    "reports.headSkipped": "Went straight to management",
    "reports.addFile": "+ Add a file",
    "reports.openFile": "Open",
    "reports.removeFile": "Remove {name}",
    "reports.history": "What happened",
    "reports.historyNone": "Nothing has been recorded on this report yet.",
    "reports.did.created": "{name} wrote it",
    "reports.did.submitted": "{name} submitted it for review",
    "reports.did.headApproved": "{name} approved it — on to management",
    "reports.did.adminApproved": "{name} approved — it reached the client",
    "reports.did.returned": "{name} returned it: “{reason}”",
    "reports.did.headReviewed": "{name} reviewed it (department head)",
    "reports.did.adminReviewed": "{name} reviewed it (management)",
    "reports.did.clientAsked": "{name} asked for a change from the portal",
    "reports.returnHint": "If you return it: what needs fixing?",
    "reports.returnPlaceholder": "Write the reason — whoever reads it needs to know what to do",
    "reports.approvePublish": "Approve — it reaches the client",
    "reports.approveHead": "Approve — on to management",
    // ---------- Moderation and publishing ----------
    "moderation.title": "Publishing queue",
    "moderation.subtitle": "What the client approved and is waiting to go out.",
    "moderation.waiting": "Waiting",
    "moderation.published": "Published",
    "moderation.schedule": "Set a date",
    "moderation.scheduleAt": "Publish at",
    "moderation.saveSchedule": "Save the time",
    "moderation.markPublished": "Mark as published",
    "moderation.proofUrl": "Link to the post on the platform",
    "moderation.proofRequired": "Add the link to the post — without it the mark has nothing behind it.",
    "moderation.publishedAt": "Published: {at}",
    "moderation.publishedBy": "Marked by: {name}",
    "moderation.withdraw": "Withdraw the mark",
    "moderation.withdrawReason": "Reason for withdrawing",
    "moderation.withdrawReasonRequired": "Write why — someone will read this months from now.",
    "moderation.withdrawn": "The mark is withdrawn and the item is back to scheduled.",
    "moderation.marked": "Marked as published.",
    "moderation.schedulePicked": "The date is set.",
    "moderation.takedownReasonRequired": "Write why.",
    "moderation.takedownOpen": "Open takedown request",
    "moderation.takedownAsked": "The request is recorded — a person has to take the post down by hand.",
    "moderation.resolution": "What exactly did you do?",
    "moderation.resolutionRequired": "Write what you did — the system does not take posts down on its own.",
    "moderation.removedIt": "I removed it from the platform",
    "moderation.refusedIt": "It stays up, and the reason is here",
    "moderation.resolved": "The request is closed.",
    "moderation.noneTitle": "The queue is empty",
    "moderation.noneBody": "Nothing is waiting to be published. As soon as a client approves an item, it appears here.",
    "moderation.takedowns": "Takedown requests",
    "moderation.client": "Client",
    "moderation.platform": "Platform",
    "moderation.late": "Past its time",
    "moderation.lateHint": "Should have gone out — publish it or book a new time",
    "moderation.lateBy": "{hours}h late",
    "moderation.undated": "No time set",
    "moderation.previewOf": "Preview of {title}",
    "moderation.noCaption": "No caption written for this item.",
    "moderation.copyCaption": "Copy caption",
    "moderation.captionCopied": "Caption copied.",
    "moderation.captionCopyFailed": "The browser blocked copying — select the caption and copy it by hand.",
    "moderation.files": "Files ({count})",
    "moderation.openPost": "Open the post",
    "moderation.confirmPublished": "Confirm — published",
    "moderation.more": "More on this post",
    "common.all": "All",
    "auth.signIn": "Sign in",
    "auth.email": "Email",
    "auth.password": "Password",
    "auth.forgot": "Forgot your password?",
    "auth.forgotTitle": "Forgot your password",
    "auth.forgotSub": "Enter your email and we will send you a link to choose a new password.",
    "auth.forgotSend": "Send me the link",
    "auth.forgotSent": "If we have that email, a link is on its way within minutes. Check your inbox — and your spam folder.",
    "auth.forgotOff": "Password recovery is not enabled on this system — ask your administrator to turn it on.",
    "auth.backToSignIn": "Back to sign in",
    "invite.subReset": "Choose a new password. As soon as you save it, every device your account is open on is signed out.",
    // ---------- Outgoing mail ----------
    "mail.reset.subject": "Your password reset link — {link}",
    "mail.reset.body": "Hello {name},\n\nWe received a request to change your password. Open this link and choose a new one:\n{link}\n\nThe link works once and expires in {minutes, plural, one {# minute} other {# minutes}}.\nIf this was not you, ignore this message — your password is unchanged.",
    "auth.signOut": "Sign out",
    // ---------- Profile: details, security and sessions ----------
    "profile.about": "My details",
    "profile.photo": "My photo",
    "profile.photoHint": "Your photo appears to colleagues anywhere your name is shown. The limit is 2 MB.",
    "profile.photoChoose": "Choose a photo",
    "profile.photoReplace": "Change photo",
    "profile.photoRemove": "Remove photo",
    "profile.photoSaved": "Your photo is saved.",
    "profile.photoRemoved": "Your photo is removed.",
    "profile.photoNone": "There was no photo to remove.",
    "profile.photoRequired": "Choose a photo first.",
    "profile.photoUploading": "Uploading your photo…",
    "profile.name": "Name",
    "profile.language": "System language",
    "profile.theme": "Appearance",
    "profile.themeSystem": "Match my device",
    "profile.themeLight": "Light",
    "profile.themeDark": "Dark",
    "profile.saved": "Your details are saved.",
    "profile.valueInvalid": "That value is not one the system accepts for this field.",
    "profile.languageNote": "Language and theme are saved to your account, so they follow you to any device. Your email is changed from People.",
    "profile.security": "Password",
    "profile.passwordChange": "Change password",
    // ---------- Sessions ----------
    "sessions.title": "Devices your account is open on",
    "sessions.thisDevice": "This device",
    "sessions.titleCount": "Devices your account is open on · {n}",
    "sessions.fromIp": "From {ip}",
    "sessions.end": "End it",
    "sessions.ended": "The device is signed out. If it was someone else, they are out now.",
    "sessions.endOthers": "End all the others",
    "sessions.endOthersConfirm": "Every other device will be signed out and will have to sign in again. This device stays open.",
    "sessions.endedOthers": "{n, plural, one {# other device was} other {# other devices were}} signed out. This device is still open.",
    "sessions.endedNone": "No other devices were open.",
    "sessions.onlyThis": "Your account is open on this device only.",
    // ---------- Device names ----------
    "device.on": "{browser} on {os}",
    "device.browserOnly": "{browser}",
    "device.osOnly": "{os}",
    "device.unknown": "Unknown device",
    "device.browser.chrome": "Chrome",
    "device.browser.safari": "Safari",
    "device.browser.firefox": "Firefox",
    "device.browser.edge": "Edge",
    "device.browser.opera": "Opera",
    "device.browser.samsung": "Samsung Internet",
    "device.os.windows": "Windows",
    "device.os.macos": "Mac",
    "device.os.ios": "iPhone",
    "device.os.ipad": "iPad",
    "device.os.android": "Android",
    "device.os.linux": "Linux",
    // ---------- shell & navigation ----------
    "nav.home": "Home",
    "nav.actions": "On you",
    "nav.actionsBadge": "waiting on you",
    "nav.chat": "Chat",
    "nav.notifications": "Notifications",
    "nav.health": "System health",
    "nav.reports": "Reports",
    "nav.analytics": "Analytics",
    "nav.moderation": "Publishing",
    "nav.storage": "Storage",
    "nav.audit": "Activity log",
    "nav.changelog": "Changelog",
    "nav.notificationsAdmin": "Notification wording",
    "nav.settings": "Settings",
    "nav.errorCatalog": "Error code catalogue",
    "nav.profile": "My profile",
    "nav.more": "More",
    "nav.group.general": "General",
    "nav.group.system": "System",
    "nav.group.me": "My account",
    "shell.skipToContent": "Skip to content",
    "shell.mainNavigation": "Main navigation",
    "shell.themeDark": "Dark mode",
    "shell.themeLight": "Light mode",
    "shell.switchLanguage": "Switch language",
    "shell.localeArabic": "العربية",
    "shell.localeEnglish": "English",
    "shell.notificationsRegion": "System messages",
    "shell.offline": "You are offline right now. We'll pick up here as soon as you're back.",
    "shell.installWhy": "Install it on this device: it opens faster and its alerts reach you.",
    "shell.install": "Install this system on your device",
    "shell.noScreenYet": "This screen hasn't shipped yet",
    "shell.noScreenYetBody": "Every screen arrives with its module. The menu above is built from your real permissions, so it grows on its own as modules land.",
    "shell.quickNavigation": "Navigation bar",
    "auth.signedOut": "You are signed out.",
    "auth.signInTitle": "Sign in to {systemName}",
    "auth.signInSubtitle": "Use your email and password.",
    "auth.signingIn": "Signing you in…",
    "auth.emailRequired": "Enter your email address.",
    "auth.passwordRequired": "Enter your password.",
    "errors.catalogTitle": "Error code catalogue",
    "errors.catalogSubtitle": "When someone sends you an error code — look it up here for its cause and its remedy.",
    "errors.catalogSearch": "Search by code or wording",
    "errors.catalogIncident": "Raises an incident",
    "errors.catalogEmpty": "No code matches that search.",
    "errors.catalogCauseLead": "Cause:",
    "errors.catalogRemedyLead": "Remedy:",
    "errors.signInRejected": "That email or password is not right. Check them and try again.",
    "tasks.blockReasonRequired": "Write why it is blocked — whoever picks it up needs to know what it is waiting on.",
    "tasks.state.open": "Open",
    "tasks.state.inProgress": "In progress",
    "tasks.state.blocked": "Blocked",
    "tasks.state.done": "Done",
    "tasks.priority.normal": "Normal",
    "tasks.priority.high": "High",
    "tasks.priority.critical": "Critical",
    "tasks.created": "The task is saved and has reached its assignees.",
    "tasks.assigned": "The assignees have changed.",
    "tasks.moved": "The task's state has changed.",
    "tasks.titleRequired": "Write the task title.",
    "tasks.dueRequired": "A task must have a due date.",
    "tasks.assigneesRequired": "Choose at least one person to assign it to.",
    "tasks.departmentRequired": "Choose the department the task belongs to.",
    "tasks.stateRequired": "Choose the state to move to.",
    "tasks.versionRequired": "The request arrived without a version — refresh the screen.",
    "notify.tasks.assigned.title": "A new task for you",
    "notify.tasks.assigned.body": "A direct task was assigned to you — open it for the due date and details.",
    "notify.tasks.updated.title": "A task changed",
    "notify.tasks.updated.body": "Someone edited a task you are part of.",
    "notify.tasks.stateChanged.title": "A task moved",
    "notify.tasks.stateChanged.body": "A task you are part of moved — open it to see where.",
    "notify.tasks.commented.title": "New comment",
    "notify.tasks.commented.body": "There is a new comment on a task you are part of.",
    "notify.platform.settingChanged.title": "A setting changed",
    "notify.platform.settingChanged.body": "Someone changed a system setting.",
    "notify.roles.matrixReplaced.title": "A role's capabilities changed",
    "notify.roles.matrixReplaced.body": "A role's capability matrix was edited — open the role to see it.",
    "notify.identity.roleAssigned.title": "Your role changed",
    "notify.identity.roleAssigned.body": "You were given a new role — your capabilities changed with it.",
    "notify.identity.overrideCreated.title": "An exceptional capability",
    "notify.identity.overrideCreated.body": "A time-limited override was recorded for you — open your profile for details.",
    "notify.identity.overrideRemoved.title": "An override was removed",
    "notify.identity.overrideRemoved.body": "An override recorded for you was removed — your capabilities follow your role again.",
    "notifications.title": "Notifications",
    "notifications.empty": "No notifications right now.",
    "notifications.emptyBody": "As soon as something concerns you, it appears here.",
    "notifications.markRead": "Mark as read",
    "notifications.markAllRead": "Mark all as read",
    "notifications.show": "Show",
    "notifications.all": "All",
    "notifications.unreadOnly": "Unread · {count}",
    "notifications.noneUnread": "You've read everything new — nothing waiting.",
    "notifications.markReadOf": "Mark “{title}” as read",
    "notifications.dotHint": "The dot means unread — press it to mark read without opening.",
    "notifications.noDestination": "This notification has no destination. Send the code to an administrator.",
    "notifications.allRead": "All marked as read.",
    "common.timezoneIs": "Timezone:",
    "common.noMatch": "Nothing matches.",
    "tasks.title": "Direct tasks",
    "tasks.subtitle": "Internal work not tied to a client — by due date.",
    "tasks.new": "New task",
    "tasks.filter": "Filter",
    "tasks.filterAll": "All",
    "tasks.filterMine": "Mine",
    "tasks.filterOverdue": "Overdue",
    "tasks.emptyTitle": "No tasks yet",
    "tasks.emptyBody": "A direct task is internal work you send to someone on your team — like “review the contract”.",
    "tasks.emptyFilteredTitle": "Nothing under this filter",
    "tasks.emptyFilteredBody": "Clear the filter to see the rest.",
    "tasks.detail": "Task details",
    "tasks.due": "Due",
    "tasks.assignees": "Assignees",
    "tasks.priority": "Priority",
    "tasks.department": "Department",
    "tasks.titleField": "Task title",
    "tasks.block": "Block it",
    "tasks.blockReason": "Why is it blocked?",
    "tasks.blockedBecause": "Blocked because:",
    "tasks.thread": "Comments",
    "tasks.noComments": "No comments yet.",
    "tasks.writeComment": "Write a comment…",
    "tasks.send": "Send",
    "tasks.saving": "Saving…",
    "tasks.edit": "Edit task",
    "tasks.changeAssignees": "Change assignees",
    "tasks.descriptionField": "Description",
    "tasks.reloadToSeeLatest": "Reload to see your colleague's edit before writing over it.",
    "tasks.newButton": "+ New task",
    "tasks.filterOverdueCount": "Overdue · {count}",
    "tasks.allDepartments": "All departments",
    "tasks.group.overdue": "Overdue",
    "tasks.group.today": "Today",
    "tasks.group.week": "This week",
    "tasks.group.later": "Later",
    "tasks.group.done": "Done",
    "tasks.markDone": "Mark “{title}” done",
    "tasks.reopen": "Reopen “{title}”",
    "tasks.filesCount": "{count, plural, one {# attached file} other {# attached files}}",
    "tasks.madeBy": "Created by {name}",
    "tasks.options": "Task options",
    "tasks.done": "Done",
    "tasks.lateSince": "Overdue · {since}",
    "tasks.editShort": "Edit",
    "tasks.files": "Files",
    "tasks.filesTitle": "Files · {count}",
    "tasks.addFile": "+ Add file",
    "tasks.openFile": "Open",
    "nav.tasks": "Tasks",
    "notifications.unreadCount": "Unread",
    "attachments.sizeRequired": "The file must have a valid size.",
    "attachments.filenameRequired": "The file must have a name.",
    "attachments.queuedForScan": "Uploaded and being scanned — it will be ready in a moment.",
    "attachments.removed": "The attachment was removed.",
    "attachments.add": "Add a file",
    "attachments.scanning": "Scanning…",
    "attachments.rejected": "Rejected by the scan",
    "attachments.none": "No attachments.",
    "attachments.uploading": "Uploading…",
    "attachments.limit": "Up to {limitMb} MB per file.",
    "units.bytes": "{n} B",
    "units.kilobytes": "{n} KB",
    "units.megabytes": "{n} MB",
    "attachments.download": "Download",
    "attachments.remove": "Remove attachment",
    "attachments.removeConfirm": "Remove “{filename}”?",
    "attachments.pendingHint": "Being scanned — it becomes downloadable when the scan finishes.",
    "errors.cardTransitionNotAllowed": "A card cannot go straight from “{from}” to “{to}” — move it through an intermediate state first.",
    "errors.reopenWindowClosed": "The reopen window is {graceMinutes, plural, one {# minute} other {# minutes}} from completion, and it has passed. This is now a department head's authority.",
    "errors.orderKeyExhausted": "The ordering at this spot hit its limit ({maxLength, plural, one {# character} other {# characters}}). Redistribute the column's order once and it is resolved.",
    "errors.clientArchived": "“{client}” is archived — its work is readable, not editable. Restore it first if work is continuing.",
    "errors.clientNotArchived": "“{client}” is still active, and this operation is for archived clients only.",
    "errors.clientDeleteNotReady": "“{client}” cannot be deleted before: {missing}",
    "errors.clientDeleteIncomplete": "Deleting “{client}” stopped at step “{step}”: {detail} — fix the cause and resume; it continues from where it stopped.",
    "errors.clientExportNotReady": "This export isn't ready to download — it is either still being built or it failed. Its state is shown next to it.",
    "plans.slidesNeedMultiSlideType": "This type is not multi-slide — change the type, or upload a single preview.",
    "plans.periodRequired": "Pick a period type — weekly, monthly or quarterly.",
    "plans.dateRequired": "The date is missing.",
    "plans.invalidDate": "That date is not valid.",
    "plans.typeRequired": "Pick a work type.",
    "plans.platformsInvalid": "Platforms must be a list of names.",
    "plans.stateRequired": "Pick the state to move it to.",
    "plans.decisionRequired": "Pick a decision — approve or reject.",
    "plans.reviewDecisionRequired": "Pick a review decision — approve or send back.",
    "plans.mediaKindRequired": "Pick the media kind.",
    "plans.typeKeyRequired": "Give the type a key — lowercase letters and dashes.",
    "plans.versionRequired": "The version is missing — refresh the screen and try again.",
    "plans.nameRequired": "Give the plan a name.",
    "plans.titleRequired": "Give the item a title.",
    "plans.reviewReasonRequired": "Say why it is going back — whoever produced it needs to know what to fix.",
    "plans.onBehalfReasonRequired": "Say why you are recording this decision on the client's behalf.",
    "plans.rejectionCommentRequired": "A rejection needs a comment saying what is wanted.",
    "plans.cancelReasonRequired": "Say why it is being cancelled.",
    "plans.caption": "Caption",
    "plans.title": "Plans",
    "plans.subtitle": "A client's plans, their items and the approval cycle",
    "plans.pickClient": "Pick a client",
    "plans.itemsTitle": "Plan items",
    "plans.item": "Item",
    "plans.type": "Type",
    "plans.state": "State",
    "plans.deliverBy": "Delivery",
    "plans.publishAt": "Publish",
    "plans.assignees": "Assigned to",
    "plans.clientSees": "Client sees",
    "plans.parked": "Parked on an exception",
    "plans.counts.published": "Published",
    "plans.counts.approved": "Approved",
    "plans.counts.working": "In progress",
    "plans.counts.cancelled": "Cancelled",
    "plans.emptyPlan": "This plan is empty — open it and add the first item.",
    "plans.openPlan": "Open plan",
    "plans.backToPlans": "‹ Plans",
    "plans.itemCount": "{n, plural, one {# item} other {# items}}",
    "plans.noPlansTitle": "No plans for this client yet",
    "plans.noPlansBody": "This is where a month's plan and its items live, with their approval cycle. Start with the first plan.",
    "plans.noItemsTitle": "This plan is still empty",
    "plans.noItemsBody": "Add the plan's items — each with its type, delivery date and who will produce it.",
    "plans.clientCannotSee": "Hidden from the client",
    "plans.revisionsUsed": "Client revisions:",
    "plans.exceptionPending": "This item is parked on a revision exception awaiting an administrative decision",
    "plans.grantException": "Grant the exception",
    "plans.denyException": "Deny the exception",
    "plans.gateOne": "Gate one — internal review",
    "plans.gateTwo": "Gate two — the client's decision",
    "plans.gateThree": "Gate three — publication",
    "plans.reviewReason": "Reason for sending back (required if you send it back)",
    "plans.agencyPublishes": "The agency publishes — approval will generate publication rows.",
    "plans.clientPublishes": "The client publishes themselves — approval closes the item straight away.",
    "plans.onBehalfReason": "Why you are recording this on the client's behalf",
    "plans.decisionComment": "The client's comment",
    "plans.recordApproval": "Record the client's approval",
    "plans.recordRejection": "Record the client's rejection",
    "plans.proofInModeration": "Proof of publication is recorded in moderation.",
    "plans.media": "Media",
    "plans.addMedia": "Add media",
    "plans.mediaKind": "Media kind",
    "plans.decisions": "Client decisions",
    "plans.approved": "Approved",
    "plans.rejected": "Rejected",
    "plans.onBehalf": "On their behalf",
    "plans.reason": "Reason",
    "plans.withdraw": "Withdraw approval",
    "plans.cancel": "Cancel the item",
    "plans.history": "History",
    "plans.created": "Plan created.",
    "plans.saved": "Changes saved.",
    "plans.deleted": "Plan deleted.",
    "plans.items.created": "Item created and assigned.",
    "plans.items.saved": "Changes saved.",
    "plans.items.moved": "Item moved.",
    "plans.items.cancelled": "Item cancelled.",
    "plans.items.decided": "Client decision recorded.",
    "plans.items.visibilitySaved": "Client visibility changed.",
    "plans.items.withdrawn": "Approval withdrawn; the item is back in production.",
    "plans.exceptions.resolved": "Exception decided.",
    "plans.media.added": "Media added.",
    "plans.media.detached": "Media detached.",
    "plans.comments.added": "Comment posted.",
    "plans.comments.saved": "Comment edited.",
    "plans.comments.deleted": "Comment deleted.",
    "plans.itemTypes.saved": "Work type saved.",
    "plans.newPlan": "New plan",
    "plans.editPlan": "Edit plan",
    "plans.planName": "Plan name",
    "plans.startsOn": "Starts on",
    "plans.endsOn": "Ends on",
    "plans.deletePlan": "Delete plan",
    "plans.deleteConfirm": "The plan “{name}” will be deleted. Only an empty plan can be deleted — one with items is refused, and you'll be told why.",
    "plans.newItem": "New item",
    "plans.itemTitle": "Item title",
    "plans.itemTypes": "Work types",
    "plans.newItemType": "New type",
    "plans.editItemType": "Edit type",
    "plans.itemTypeKey": "Key — lowercase English, like reel",
    "plans.itemTypeNeedsCover": "Needs a cover image",
    "plans.itemTypeMultiSlide": "Multi-slide (carousel)",
    "plans.detachMedia": "Detach",
    "plans.detachConfirm": "Detach this file from the item? If it was a preview, the client won't see it any more.",
    "clients.publishingSaved": "Publishing responsibility saved.",
    "plans.missing.preview": "a client preview",
    "plans.task.state.not_started": "Not started",
    "plans.task.state.in_progress": "In progress",
    "plans.task.state.delivered": "Delivered",
    "plans.task.state.approved": "Approved",
    "plans.task.state.returned": "Returned",
    "plans.task.dueAfterItem": "The task is due after the item's delivery date — it must be on or before it.",
    "plans.deliverBeforeTasks": "A task is due after the new delivery date — change its date first.",
    "plans.decide.partsRequired": "Choose which part goes back for changes — the reel, the cover, or both.",
    "plans.task.nameRequired": "The task needs a name.",
    "plans.task.coverRequired": "This type needs a cover — add a cover task.",
    "plans.priorityRequired": "Choose the item's priority.",
    "plans.task.ownerRequired": "Choose who owns the task.",
    "plans.referencesInvalid": "References must be links.",
    "plans.priority": "Priority",
    "plans.tasks": "Tasks",
    "plans.tov": "Text on visual (TOV)",
    "plans.tov.hint": "The words written on the video or design itself — the designer and editor work from it.",
    "plans.cancelHint": "A cancelled item stays in the plan as a record — write the reason.",
    "plans.withdrawHint": "Withdrawing approval returns every task to its owner and cancels publications not yet out.",
    "plans.caption.revokes": "Editing it after the client approved sends it back for their decision.",
    "plans.compose.addTask": "+ Task",
    "plans.compose.contentLater": "The brief, references, caption and TOV are added after the item exists, from the «Content» tab.",
    "plans.compose.removeTask": "Remove task “{name}”",
    "plans.compose.requiredHint": "Fields marked * are required — without them the designer cannot plan the work.",
    "plans.compose.submit": "Add item",
    "plans.compose.tasksTitle": "Tasks — suggested by “{type}”",
    "plans.decide.partsLegend": "If rejected — which part goes back?",
    "plans.deliverByHint": "When the item must be ready for review",
    "plans.publishAtHint": "Sets its priority in the designer's schedule",
    "plans.exceptionNote": "Note on the decision (optional)",
    "plans.gate.internal": "Internal",
    "plans.gate.client": "Client",
    "plans.gate.publish": "Publishing",
    "plans.gates": "Gates",
    "plans.itemMenu": "Item options",
    "plans.media.none": "No brief or materials yet.",
    "plans.part.clientApproved": "Client approved",
    "plans.publish.afterApproval": "They enter the publishing queue once the client approves every part.",
    "plans.references": "References",
    "plans.references.add": "+ Link",
    "plans.references.none": "No references yet.",
    "plans.references.remove": "Remove link {link}",
    "plans.tab.work": "Work",
    "plans.tab.content": "Content",
    "plans.tab.review": "Review & client",
    "plans.tab.talk": "Discussion",
    "plans.tabs": "Item sections",
    "plans.talk.hint": "This item's discussion only — with @mentions and files. Internal notes never reach the client.",
    "plans.work.hint": "Each task has its owner and due date — lateness counts on the task, not the whole item.",
    "plans.task.approve": "Approve",
    "plans.task.deliver": "Deliver task",
    "plans.task.due": "Due",
    "plans.task.dueOn": "due {date}",
    "plans.task.edit": "Edit",
    "plans.task.final": "Final delivery",
    "plans.task.finalLink": "Final delivery link",
    "plans.task.isCover": "Its preview is the item's cover",
    "plans.task.late": "Late",
    "plans.task.name": "Task",
    "plans.task.notYet": "Not yet",
    "plans.task.owner": "Who",
    "plans.task.pickOwner": "Choose the task owner",
    "plans.task.preview": "Client preview",
    "plans.task.previewFirst": "Upload the preview first — no delivery without it.",
    "plans.task.previewLink": "or a preview link",
    "plans.task.remove": "Remove",
    "plans.task.removeConfirm": "Remove task “{name}”?",
    "plans.task.replacePreview": "Replace preview",
    "plans.task.return": "Return with a reason",
    "plans.task.returnedBy": "Returned from review:",
    "plans.task.returnedByClient": "The client returned it:",
    "plans.task.sendReturn": "Return it",
    "plans.task.start": "Start",
    "plans.task.uploadPreview": "Upload preview",
    "plans.view": "View",
    "plans.view.pipeline": "Pipeline",
    "plans.view.table": "Table",
    "plans.openItem": "Open “{title}”",
    "plans.preview.none": "No preview yet",
    "plans.lane.rule": "Every item passes three gates: internal review → client approval → publishing. Tap an item to open it.",
    "plans.lane.working": "In progress",
    "plans.lane.workingHint": "Draft · assigned · in progress · returned",
    "plans.lane.review": "Internal review",
    "plans.lane.reviewHint": "Awaiting approval before it goes to the client",
    "plans.lane.client": "With the client",
    "plans.lane.clientHint": "Approved internally — awaiting the client's decision",
    "plans.lane.scheduled": "Approved & scheduled",
    "plans.lane.scheduledHint": "The client approved — in the publishing queue",
    "plans.lane.published": "Published",
    "plans.lane.publishedHint": "Out with proof — the post link is in its panel",
    "plans.lane.cancelled": "{n} cancelled — show them",
    "plans.itemTypeTasks": "Tasks this type suggests",
    "plans.itemTypeTasksHint": "They appear ready when someone creates an item of this type — and can be changed. A type that needs a cover must suggest a cover task.",
    "plans.itemTypeTaskAr": "Task {n} in Arabic",
    "plans.itemTypeTaskEn": "Task {n} in English",
    "plans.itemTypeTaskRemove": "Remove task {n}",
    "priority.urgent": "Urgent",
    "priority.high": "High",
    "priority.normal": "Normal",
    "priority.low": "Low",
    "errors.planTaskMoveInvalid": "The task cannot move that way while it is “{from}” — someone likely moved it before you.",
    "plans.state.draft": "Draft",
    "plans.state.assigned": "Assigned",
    "plans.state.in_progress": "In progress",
    "plans.state.internal_review": "Internal review",
    "plans.state.internal_revision": "Sent back",
    "plans.state.approved_internally": "Approved internally",
    "plans.state.client_approved": "Client approved",
    "plans.state.scheduled": "Scheduled",
    "plans.state.published": "Published",
    "plans.state.done": "Done",
    "plans.state.cancelled": "Cancelled",
    "plans.period.weekly": "Weekly",
    "plans.period.monthly": "Monthly",
    "plans.period.quarterly": "Quarterly",
    "plans.media.brief": "Brief",
    "plans.media.material": "Reference material",
    "plans.media.deliverable": "Deliverable",
    "plans.media.preview": "Client preview",
    "plans.media.cover": "Cover image",
    "plans.media.slide": "Carousel slides",
    "plans.publication.pending": "Queued",
    "plans.publication.scheduled": "Scheduled",
    "plans.publication.published": "Published",
    "plans.publication.failed": "Failed",
    "plans.publication.cancelled": "Cancelled",
    "plans.assignee.notMember": "not on the client's team",
    "plans.assignee.notActive": "the account is suspended",
    "plans.assignee.departmentNotAssignable": "their department does not take plan work",
    "errors.planTransitionInvalid": "This item cannot move from {from} to {to}.",
    "errors.planItemIncomplete": "The task is missing what delivery requires.",
    "errors.planReviewSelf": "You cannot review a task you own.",
    "errors.planItemNotDecidable": "The item is not in a state that accepts a client decision ({state}).",
    "errors.planPublishedEditRefused": "The item is {state} — withdraw the approval before editing.",
    "errors.planLastAssignee": "An item must keep at least one task.",
    "errors.planAssigneeNotEligible": "“{user}” cannot take this item — {reason}.",
    "errors.planExceptionPending": "A revision exception is already open on this item.",
    "errors.planCancelInvalidState": "The item is {state} — it cannot be cancelled.",
    "errors.planNameDuplicate": "A plan named “{name}” already exists for this client and period.",
    "errors.planDateRangeInvalid": "A plan must end after it starts.",
    "errors.planNotEditRequestable": "The item is {state} — there is no approval to revisit.",
    "errors.planAlreadyWithdrawn": "The approval is already withdrawn and the item is back in production.",
    "errors.planMediaSourceInvalid": "Pick a file or a link — one of the two.",
    "errors.confirmationNameMismatch": "Type the name exactly as shown: “{expected}” — in Arabic or in English.",
    "errors.dueChangeAlreadyPending": "A due-change request from {requestedBy} is already pending on this card. Wait for it or withdraw it.",
    "errors.dueChangeAlreadyResolved": "{decidedBy} already decided this request. Refresh to see the decision.",
    "errors.boardLastColumn": "This is the board's last column — add a replacement first, or archive the whole board.",
    "errors.columnDeleteNeedsTarget": "The column holds {cardCount, plural, one {# card. Choose the column it moves to.} other {# cards. Choose the column they move to.}}",
    "errors.mentionNotRelevant": "“{department}” has no declared connection to this work, and a department mention reaches its whole roster.",
    "errors.uniqueValueTaken": "That value is already used in “{entity}” ({constraint}) — change it and try again.",
    "errors.databaseConstraint": "Something else depends on “{entity}” ({constraint}) — remove that first.",
    "clients.title": "Clients",
    "clients.created": "Client added.",
    "clients.updated": "Client details saved.",
    "clients.archived": "Client archived — its work is now read-only.",
    "clients.restored": "Client is active again.",
    "clients.deleted": "Client permanently deleted.",
    "clients.exportStarted": "Export started — you will be told when it completes.",
    "clients.memberAdded": "{name} is on the client's team — and now sees its work.",
    "clients.memberRemoved": "{name} was removed from the client's team.",
    "clients.accountManagerSet": "{name} is now the client's account manager.",
    "clients.quotaSaved": "Storage limit saved.",
    "storage.orphans.title": "Orphaned files — need a decision",
    "storage.orphans.subtitle": "A file in storage with no database row (or the reverse). The hourly check finds them.",
    "storage.orphans.hidden": "Rows outside your scope, count:",
    "storage.orphans.storageOnly": "Bytes with no row",
    "storage.orphans.rowOnly": "Row with no bytes",
    "storage.orphans.noClient": "Owner unknown",
    "storage.orphans.emptyTitle": "Storage and the database agree",
    "storage.orphans.emptyBody": "This lists any file held in storage with no row in the database, or the reverse. Reconciliation runs hourly and currently finds no difference.",
    "clients.nameRequired": "The client's name is required in both Arabic and English.",
    "clients.keyRequired": "A client key is required — lowercase letters and dashes.",
    "clients.timezoneRequired": "The client's timezone is required — its screens render in it.",
    "clients.accountManagerNotMember": "The account manager must be on the client's team.",
    "clients.memberNotActive": "That employee is inactive — an inactive person is not added to a client's team.",
    "clients.lastAccountManager": "This is the account manager — mark someone else before removing them.",
    "clients.archiveConfirm": "Archive “{name}”? All its work becomes read-only.",
    "clients.deleteImpact": "Will be deleted: {cards, plural, one {# card} other {# cards}} · {boards, plural, one {# board} other {# boards}} · {files, plural, one {# file} other {# files}} · {members, plural, one {# team member} other {# team members}}.",
    "clients.deleteMissingArchive": "archiving it",
    "clients.deleteMissingExport": "a completed export",
    "clients.storage": "Storage",
    "clients.storageUsed": "{used} of {quota} used ({percent}%)",
    "clients.team": "Team",
    "clients.accountManager": "Account manager",
    "clients.archivedBadge": "Archived",
    "boards.title": "Boards",
    "boards.created": "Board created.",
    "boards.updated": "Board saved.",
    "boards.archived": "Board archived.",
    "boards.restored": "Board restored.",
    "boards.columnAdded": "Column added.",
    "boards.columnUpdated": "Column saved.",
    "boards.columnRemoved": "Column removed.",
    "boards.reordered": "Order saved.",
    "boards.templateSaved": "Template saved.",
    "boards.templateRemoved": "Template deleted.",
    "boards.nameRequired": "A board name is required.",
    "boards.columnNameRequired": "A column name is required.",
    "boards.columnsRequired": "A template needs at least one column.",
    "boards.columnNameAr": "Column name (Arabic)",
    "boards.columnNameEn": "Column name (English)",
    "boards.columnRemove": "Remove the column",
    "boards.columnRemoveConfirm": "Remove this column? It's empty, and you can add another later.",
    "boards.columnMoveStart": "Move it earlier",
    "boards.columnMoveEnd": "Move it later",
    "boards.create": "New board",
    "boards.nameAr": "Board name (Arabic)",
    "boards.nameEn": "Board name (English)",
    "boards.templateNone": "Without a template",
    "boards.templateNoneHint": "It starts with default columns you can change later.",
    "boards.template": "Template",
    "boards.noTemplate": "No template",
    "cards.title": "Cards",
    "cards.created": "Card created.",
    "cards.updated": "Card saved.",
    "cards.stateChanged": "Card state changed.",
    "cards.archived": "Card archived for everyone.",
    "cards.restored": "Card restored — and its recurrence with it.",
    "cards.hidden": "Card hidden from your view only.",
    "cards.unhidden": "Card is back in your list.",
    "cards.assigned": "Assignment saved.",
    "cards.commented": "Comment posted.",
    "cards.commentEdited": "Comment edited — the previous revision is kept.",
    "cards.commentRemoved": "Comment deleted.",
    "cards.labelCreated": "Label created.",
    "cards.labelUpdated": "Label saved.",
    "cards.labelRemoved": "Label removed from the catalogue.",
    "cards.checklistSaved": "Checklist saved.",
    "cards.titleRequired": "A card title is required.",
    "cards.dueRequired": "A due date is required — the only penalty for lateness is ranking, and it does not work without one.",
    "cards.assigneesRequired": "Choose at least one assignee.",
    "cards.assigneeNotActive": "{name} is deactivated and cannot be added to a card.",
    "cards.blockReasonRequired": "Write why it is blocked — whoever picks it up needs to know what it waits on.",
    "comments.mentionNotOnRecord": "You cannot mention someone with no connection to this work.",
    "cards.checklistItemRequired": "Write the checklist item.",
    "cards.labelNameRequired": "A label name is required.",
    "cards.columnRequired": "Choose the column.",
    "cards.state.open": "Open",
    "cards.state.inProgress": "In progress",
    "cards.state.blocked": "Blocked",
    "cards.state.done": "Done",
    "cards.state.reopened": "Reopened",
    "cards.checklist": "Checklist",
    "cards.labels": "Labels",
    "cards.overdue": "Overdue",
    "cards.archivedBadge": "Archived",
    "cards.due.request": "Request a due-date change",
    "cards.due.requested": "The request reached the department head and the client's account manager — whoever decides first closes it.",
    "cards.due.approved": "The due date changed, and reminders were rescheduled.",
    "cards.due.rejected": "The request was rejected.",
    "cards.due.changed": "Due date changed.",
    "cards.due.reasonRequired": "Write why you are requesting the change.",
    "cards.due.newDateRequired": "Choose the new due date.",
    "cards.due.pending": "Due-date change pending",
    "cards.due.requestedBy": "{name} asked for {date}",
    "cards.recurrence": "Recurrence",
    "cards.recurrence.none": "Does not repeat",
    "cards.recurrence.daily": "Daily",
    "cards.recurrence.weekly": "Weekly",
    "cards.recurrence.monthly": "Monthly",
    "cards.recurrence.nextRun": "Next: {date}",
    "cards.recurrence.monthlyClamped": "A month without day {day} uses its last day.",
    "cards.recurrence.stopped": "Recurrence stopped with archiving — restoring brings it back.",
    "cards.reminder.due": "“{title}” is due {when}.",
    "cards.escalation.late": "“{title}” is {days, plural, one {# day} other {# days}} late and its assignee has not moved it.",
    "myWork.title": "My work",
    "myWork.cards": "Client cards",
    "myWork.tasks": "Direct tasks",
    "notify.clients.created": "New client",
    "notify.clients.createdBody": "{actor} added “{subject}”.",
    "notify.clients.memberAddedTitle": "You joined a client's team",
    "notify.clients.memberAddedBody": "{actor} added you to “{subject}” — that client's work is now visible to you.",
    "notify.clients.memberRemovedTitle": "You left a client's team",
    "notify.clients.memberRemovedBody": "{actor} removed you from “{subject}”.",
    "notify.clients.managerTitle": "Client account manager",
    "notify.clients.managerBody": "{actor} made you account manager of “{subject}” — you now decide due-date change requests.",
    "notify.clients.archivedTitle": "Client archived",
    "notify.clients.archivedBody": "{actor} archived “{subject}” — its work is now read-only and cannot be edited.",
    "notify.clients.restoredTitle": "Client restored",
    "notify.clients.restoredBody": "{actor} restored “{subject}” — work on it is open again, and recurrence is back.",
    "notify.clients.exportTitle": "Client data export",
    "notify.clients.exportBody": "{actor} requested an export of “{subject}”.",
    "notify.clients.deletedTitle": "Client permanently deleted",
    "notify.clients.deletedBody": "{actor} permanently deleted “{subject}”, after archiving and exporting it.",
    "notify.clients.quotaTitle": "Storage limit changed",
    "notify.clients.quotaBody": "{actor} changed the storage limit for “{subject}”.",
    "notify.clients.quotaThresholdTitle": "Client storage filling up",
    "notify.clients.quotaThresholdBody": "“{subject}” has reached {percent}% of its storage. Past {block}% uploads stop; reading and deleting keep working.",
    "notify.clients.archiveReminderTitle": "Client archived a while ago",
    "notify.clients.archiveReminderBody": "“{subject}” has been archived for {weeks, plural, one {# week} other {# weeks}}. Delete it or restore it? This reminder repeats four times only.",
    "notify.boards.archivedTitle": "Board archived",
    "notify.boards.archivedBody": "{actor} archived the board “{subject}”.",
    "notify.cards.assignedTitle": "A card assigned to you",
    "notify.cards.assignedBody": "{actor} assigned you “{subject}”.",
    "notify.cards.updatedTitle": "Card edited",
    "notify.cards.updatedBody": "{actor} edited “{subject}”.",
    "notify.cards.movedTitle": "Card moved",
    "notify.cards.movedBody": "{actor} moved “{subject}” to another column — its state did not change.",
    "notify.cards.stateTitle": "Card state changed",
    "notify.cards.stateBody": "{actor} changed the state of “{subject}”.",
    "notify.cards.archivedTitle": "Card archived",
    "notify.cards.archivedBody": "{actor} archived “{subject}” for everyone — its recurrence stopped.",
    "notify.cards.restoredTitle": "Card restored",
    "notify.cards.restoredBody": "{actor} restored “{subject}” — its recurrence is back.",
    "notify.cards.commentTitle": "New comment",
    "notify.cards.commentBody": "{actor} commented on “{subject}”.",
    "notify.cards.mentionTitle": "{actor} mentioned you",
    "notify.cards.mentionBody": "In a comment on “{subject}”.",
    "notify.cards.dueRequestedTitle": "Due-date change requested",
    "notify.cards.dueRequestedBody": "{actor} asked to change the due date of “{subject}”. You or the department head — whoever decides first closes it.",
    "notify.cards.dueDecidedTitle": "Due-date request decided",
    "notify.cards.dueDecidedBody": "{actor} decided the due-date request on “{subject}”.",
    "notify.cards.dueChangedTitle": "Card due date changed",
    "notify.cards.dueChangedBody": "{actor} changed the due date of “{subject}” — reminders were rescheduled.",
    "notify.cards.reminderTitle": "Due soon",
    "notify.cards.reminderBody": "“{subject}” is due soon.",
    "notify.cards.overdueTitle": "Card past due",
    "notify.cards.overdueBody": "“{subject}” has been past due for {days, plural, one {# day} other {# days}} — still open.",
    "notify.cards.escalationTitle": "A late card on your team",
    "notify.cards.stalledTitle": "A recurring task is stuck",
    "notify.cards.stalledBody": "“{subject}” has {missed, plural, one {# late copy that nobody closes} other {# late copies in a row that nobody closes}} — this alert repeats every 3 days until it is resolved.",
    "notify.cards.escalationBody": "“{subject}” is past due and its assignee has not moved it — escalation reaches you after two days, then daily.",
    "notify.clients.quotaThresholdActor": "The system",
    "common.working": "Working…",
    "confirm.typeName": "Type “{name}” to confirm",
    "confirm.otherNameAccepted": "“{name}” is accepted too.",
    "clients.new": "New client",
    "clients.edit": "Edit client details",
    "clients.muteReminder": "Mute the archive reminder",
    "clients.publishingToAgency": "Let the agency publish",
    "clients.publishingToClient": "Let the client publish",
    "clients.filter": "Filter clients",
    "clients.emptyTitle": "No clients yet",
    "clients.emptyBody": "A client is the unit everything hangs on: its boards, cards, files, and team.",
    "clients.emptyArchivedTitle": "No archived clients",
    "clients.emptyArchivedBody": "An archived client's work is readable but not editable, and stays until it is deleted after export.",
    "clients.cardCount": "{n, plural, one {# card} other {# cards}}",
    "clients.boardsLabel": "Boards",
    "clients.quotaGb": "Storage in gigabytes",
    "clients.uploadsStopped": "Storage is full — uploads are stopped; reading and deleting keep working.",
    "clients.archive": "Archive",
    "clients.archiveTitle": "Archive the client",
    "clients.restore": "Restore",
    "clients.export": "Export",
    "clients.delete": "Delete permanently",
    "clients.deleteTitle": "Permanently delete the client",
    "clients.deleteConfirm": "“{name}” and all its work will be permanently deleted with no way back. Only the name stays in the audit log.",
    "clients.deleteBlocked": "Not ready for deletion — missing: {missing}",
    "clients.archivedNoticeTitle": "This client is archived.",
    "clients.archivedNoticeBody": "{who} archived it on {when} — its work is readable, not editable. Restore it if work is continuing.",
    "clients.exportReadyTag": "Ready",
    "clients.exportRunningTag": "Running",
    "clients.exportFailedTag": "Failed",
    "clients.addMember": "Add to team",
    "clients.addMemberPick": "Choose the employee",
    "clients.addMemberMeans": "Whoever you add will see this client's work — and so will their department head, because a client's departments come from its team's departments.",
    "clients.makeManager": "Make account manager",
    "clients.memberInactive": "Inactive",
    "clients.teamEmpty": "Nobody on the team — and with no team, nobody sees this client's work.",
    "clients.keyLabel": "Client key",
    "clients.keyHint": "Lowercase letters and dashes — it appears in links and exports.",
    "clients.timezoneLabel": "Client timezone",
    "clients.timezoneHint": "The client's own screens render in this zone. Staff screens use the system zone.",
    "clients.newButton": "+ New client",
    "clients.activeSummary": "{n, plural, one {# active client} other {# active clients}} — the ones that need a look come first.",
    "clients.search": "Search clients…",
    "clients.filterActiveCount": "Active · {n}",
    "clients.filterArchivedCount": "Archived · {n}",
    "clients.searchNoneTitle": "No client by that name",
    "clients.searchNoneBody": "Try part of the Arabic or English name, or the client code.",
    "clients.amLine": "Account manager: {name}",
    "clients.amNone": "No account manager yet",
    "clients.planOfMonth": "{month} plan",
    "clients.planLine": "{n, plural, one {# item} other {# items}} · {published} published",
    "clients.noPlanThisMonth": "No plan for this month",
    "clients.boardsCards": "{boards, plural, one {# board} other {# boards}} · {cards, plural, one {# card} other {# cards}}",
    "clients.lateCount": "{n} late",
    "clients.awaitingCount": "{n} awaiting the client",
    "clients.storagePercent": "Storage {n}%",
    "clients.back": "‹ Clients",
    "clients.amShort": "Account manager {name}",
    "clients.tzShort": "{tz} time",
    "clients.editButton": "Edit details",
    "clients.more": "More — export · archive",
    "clients.lateLabel": "Late",
    "clients.lateCards": "{n, plural, one {# card} other {# cards}}",
    "clients.storageOf": "{used} of {quota} GB",
    "clients.tabs": "Client sections",
    "clients.tabPortal": "Portal & contacts",
    "clients.tabSettings": "Storage & labels",
    "clients.tabData": "Export & archive",
    "clients.teamCount": "Team · {n}",
    "clients.addMemberButton": "+ Add to team",
    "clients.teamHint": "Team members see this client's work. The account manager decides due-date change requests.",
    "clients.removeMemberNamed": "Remove {name} from the team",
    "clients.publishingQuestion": "Who publishes this client's work?",
    "clients.contactsCount": "Contacts · {n}",
    "clients.addContactButton": "+ Add a contact",
    "clients.contactRemoveNamed": "Remove {name}",
    "clients.quotaTitle": "Storage limit",
    "clients.quotaHint": "Once storage reaches the stop threshold, uploads stop; reading and deleting keep working.",
    "clients.gb": "GB",
    "clients.labelsTitle": "Card labels",
    "clients.labelsSummary": "{n, plural, one {# label} other {# labels}}: {names}",
    "clients.labelsCount": "{n, plural, one {# label} other {# labels}}",
    "clients.manageLabels": "Manage labels",
    "clients.exportTitle": "Export all client data",
    "clients.exportHint": "Plans, cards, reports and chat — one file.",
    "clients.exportLast": "Last export: {when} · {state}",
    "clients.exportNow": "Export now",
    "clients.exportDownload": "Download",
    "clients.exportDownloadLast": "Download latest export",
    "clients.archiveRowTitle": "Archive client",
    "clients.archiveHint": "Their work becomes read-only and recurrence stops. You can restore them any time.",
    "clients.archiveButton": "Archive…",
    "clients.deleteRowTitle": "Delete permanently",
    "clients.deleteHint": "Available only after archiving and a completed export — and by typing the client's name.",
    "clients.deleteButton": "Delete permanently…",
    "week.sunday": "Sunday",
    "week.monday": "Monday",
    "week.tuesday": "Tuesday",
    "week.wednesday": "Wednesday",
    "week.thursday": "Thursday",
    "week.friday": "Friday",
    "week.saturday": "Saturday",
    "boards.backToClients": "Clients",
    "boards.columnCount": "{n, plural, one {# column} other {# columns}}",
    "boards.columnEmpty": "No cards here",
    "boards.emptyTitle": "No boards",
    "boards.emptyBody": "A board is where a client's work lives: its columns are locations, and each card carries its own state.",
    "boards.readOnlyBecauseArchived": "The client is archived, so all its work is read-only. Restore it from its profile if work is continuing.",
    "cards.new": "New card",
    "cards.detail": "Card",
    "cards.titleLabel": "Card title",
    "cards.editText": "Edit title and description",
    "cards.peopleAndLabels": "People and labels",
    "cards.saveAssignees": "Save people",
    "cards.labelsLabel": "Labels",
    "cards.saveLabels": "Save labels",
    "cards.hideForMe": "Hide from my view",
    "cards.unhideForMe": "Show in my view again",
    "cards.descriptionLabel": "Description",
    "cards.dueLabel": "Due",
    "cards.dueTitle": "Due date",
    "cards.assigneesLabel": "Assigned to",
    "cards.assigneesFromTeam": "Only the client's active team members appear here — someone off the team cannot be assigned.",
    "cards.blockedBecause": "Blocked because:",
    "cards.blockReasonLabel": "Why it is blocked",
    "cards.blockReasonHint": "Whoever picks it up needs to know exactly what it waits on — not just “blocked”.",
    "cards.archivedNotice": "This card is archived.",
    "cards.archivedNoticeBody": "{who} archived it for everyone and its recurrence stopped. Restoring brings both back.",
    "cards.archiveAction": "Archive for everyone",
    "cards.restoreAction": "Restore",
    "cards.reopenedAt": "Reopened",
    "cards.checklistEmpty": "No items yet.",
    "cards.addChecklistItem": "New item",
    "cards.due.pendingShort": "due",
    "cards.due.newDate": "New due date",
    "cards.due.reasonLabel": "Why you are asking",
    "cards.due.reasonHint": "Whoever decides needs to know on what basis — a request without a reason cannot be decided.",
    "cards.due.changeDirectly": "Change the due date",
    "cards.due.changeReschedules": "Reminders will be rescheduled to the new date, and any pending request will be closed.",
    "cards.due.approve": "Approve",
    "cards.due.reject": "Reject",
    "cards.due.withdraw": "Withdraw",
    "cards.due.approvedTag": "Approved",
    "cards.due.rejectedTag": "Rejected",
    "cards.due.withdrawnTag": "Withdrawn",
    "cards.repeats": "This card repeats",
    "cards.repeatsHint": "Recurrence is set with creation in one request — if it is refused, the whole card is refused and you see why.",
    "cards.everyN": "Every how many",
    "cards.monthDay": "Day of month",
    "cards.weekDay": "Day of week",
    "cards.recurrenceZone": "Dates are calculated in {zone} — and it is actually read, not merely stored.",
    "myWork.subtitle": "Everything on you — cards, tasks and plan-item tasks — by due date.",
    "myWork.lane": "Kind of work",
    "myWork.noCardsTitle": "No cards on you",
    "myWork.noCardsBody": "When someone assigns you a client card it appears here — finished ones leave the list.",
    "myWork.noTasksTitle": "No tasks on you",
    "myWork.noTasksBody": "Direct tasks are internal work with no client — the ones on you appear here.",
    "myWork.all": "All",
    "myWork.allCount": "All · {count}",
    "myWork.planTasks": "Plan tasks",
    "myWork.kind.card": "Card",
    "myWork.kind.plan": "Plan task",
    "myWork.kind.task": "Task",
    "myWork.noneTitle": "Nothing on you",
    "myWork.noneBody": "When a card, a task or a plan task is assigned to you, it shows up here by its due date.",
    "myWork.noPlanTasksTitle": "No plan tasks on you",
    "myWork.noPlanTasksBody": "Plan-item tasks you own show up here until they are approved.",
    "myWork.returnedBecause": "Returned: “{reason}”",
    "myWork.delivered": "Delivered, awaiting review",
    "nav.clients": "Clients",
    "nav.boards": "Boards",
    "labels.title": "Client labels",
    "labels.none": "No labels yet — add the first one below.",
    "labels.onCards": "On {n, plural, one {# card} other {# cards}}",
    "labels.new": "New label",
    "labels.edit": "Edit label",
    "labels.tone": "Colour",
    "labels.tone.neutral": "Grey",
    "labels.tone.info": "Blue",
    "labels.tone.success": "Green",
    "labels.tone.warning": "Yellow",
    "labels.tone.danger": "Red",
    "labels.delete": "Delete label",
    "labels.deleteConfirm": "The label will be removed from {n, plural, one {# card} other {# cards}} and deleted from the client's catalogue.",
    "boards.rename": "Rename board",
    "boards.archiveBoard": "Archive board",
    "boards.archiveConfirm": "The board will be archived and leave the list — you can restore it later. Continue?",
    "boards.restoreBoard": "Restore board",
    "boards.archivedNotice": "This board is archived — locked for edits until it is restored.",
    "boards.manageLabels": "Labels",
    "boards.templates": "Board templates",
    "boards.newTemplate": "New template",
    "boards.editTemplate": "Edit template",
    "boards.templateKey": "Key — lowercase English and dashes, like content-flow",
    "boards.templateActive": "Offered when creating a new board",
    "boards.templateInactive": "Paused",
    "boards.templateColumns": "Template columns, in order",
    "boards.templateColumnAr": "Column {n} Arabic name",
    "boards.templateColumnEn": "Column {n} English name",
    "boards.removeColumnN": "Remove column {n}",
    "boards.addTemplateColumn": "Add column",
    "boards.deleteTemplate": "Delete template",
    "boards.deleteTemplateConfirm": "The template will be deleted. Boards made from it ({n}) keep their columns as they are.",
    "boards.showArchived": "Archived",
    "boards.boardMenu": "Board options",
    "boards.columnNew": "New column",
    "boards.addColumn": "+ New column",
    "boards.columnRenameTitle": "Rename column",
    "boards.columnMenu": "Options for column {name}",
    "boards.cardHere": "New card here",
    "boards.addCardIn": "+ Card in “{name}”",
    "boards.columnRemoveLast": "Remove column — it's the board's last column",
    "boards.columnRemoveBlocked": "Remove column — it holds {n, plural, one {# card, move it first} other {# cards, move them first}}",
    "boards.meta": "{client} · {open, plural, one {# open card} other {# open cards}} · {columns, plural, one {# column} other {# columns}}",
    "boards.search": "Search cards",
    "boards.searchHint": "Search cards…",
    "boards.filterMine": "My cards",
    "boards.filterLate": "Late · {n}",
    "boards.filterLabels": "By label",
    "boards.filterGrouped": "Group repeats",
    "boards.dragHint": "Drag cards between columns — a column is a place, not a state",
    "boards.columnTabs": "Board columns",
    "boards.filteredEmpty": "No cards here match this filter",
    "boards.seriesLate": "Repeating card · {n, plural, one {# late copy} other {# late copies}} — open the group",
    "boards.seriesCopies": "Repeating card · {n, plural, one {# open copy} other {# open copies}} — open the group",
    "boards.moveCard": "Move to column",
    "boards.moveCardTitle": "Move “{title}” to a column",
    "boards.moveHere": "{name} — it's here now",
    "boards.clientsFilter": "Clients",
    "boards.allClients": "All clients",
    "boards.groupMeta": "{boards, plural, one {# board} other {# boards}} · {cards, plural, one {# card} other {# cards}}",
    "boards.subtitle": "Every board belongs to a client; its columns come from its template.",
    "boards.lateCount": "{n} late",
    "boards.archivedBadge": "Archived",
    "cards.archivedBySystem": "The system archived it automatically because it was done before last month began. Restoring brings it back.",
    "cards.priority": "Priority",
    "cards.context": "{client} · {board} · in “{column}”",
    "cards.stateTitle": "Status",
    "cards.teamTitle": "Team and labels",
    "cards.editPeople": "Edit team and labels",
    "cards.noAssignees": "No one is on this card yet.",
    "cards.madeBy": "Created by {name} · {date}",
    "nav.plans": "Plans",
    "nav.myWork": "My work",
    "notify.cards.attachedTitle": "New attachment",
    "notify.cards.attachedBody": "{actor} attached a file to “{subject}”.",
    "notify.cards.recurrenceSpawnedTitle": "New recurring card",
    "notify.cards.recurrenceSpawnedBody": "“{subject}” opened for its new cycle, due {due}.",
    "notify.clients.exportReadyTitle": "Client export ready",
    "notify.clients.exportReadyBody": "The data export you requested for “{subject}” is ready to download.",
    "notify.clients.exportFailedTitle": "Client export failed",
    "notify.clients.exportFailedBody": "The export for “{subject}” did not complete — code {code}. Deletion stays blocked until an export succeeds.",
    "notify.plans.assignedTitle": "A plan item is yours",
    "notify.plans.assignedBody": "“{subject}” was assigned to you — due {due}.",
    "notify.plans.taskAssignedTitle": "A new task for you",
    "notify.plans.taskAssignedBody": "“{task}” on “{subject}” is yours — due {due}.",
    "notify.plans.taskDeliveredTitle": "A task was delivered for review",
    "notify.plans.taskDeliveredBody": "“{task}” on “{subject}” is ready for internal review.",
    "notify.plans.taskApprovedTitle": "Your task was approved",
    "notify.plans.taskApprovedBody": "“{task}” on “{subject}” was approved internally.",
    "notify.plans.taskReturnedTitle": "Your task came back for changes",
    "notify.plans.taskReturnedBody": "“{task}” on “{subject}” came back: {reason}",
    "plans.task.added": "Task added.",
    "plans.task.saved": "Task saved.",
    "plans.task.reassigned": "The task moved to its new owner, who has been told.",
    "plans.task.removed": "Task removed.",
    "plans.task.started": "Task started.",
    "plans.task.previewAdded": "Preview uploaded.",
    "plans.task.deliveredToast": "Task delivered — the reviewers have been told.",
    "plans.task.approvedToast": "Task approved.",
    "plans.task.reviewedToast": "Review recorded.",
    "plans.task.returnedToast": "The task went back to its owner with your reason.",
    "plans.task.itemApprovedToast": "The last task was approved — the item is approved internally and the client has been told.",
    "notify.plans.updatedTitle": "A plan item changed",
    "notify.plans.updatedBody": "{actor} edited “{subject}”.",
    "notify.plans.stateTitle": "An item's state changed",
    "notify.plans.stateBody": "{actor} moved “{subject}”.",
    "notify.plans.awaitingTitle": "Waiting on the client",
    "notify.plans.awaitingBody": "“{subject}” is ready and needs the client's decision — record it if they approved elsewhere.",
    "notify.plans.decidedTitle": "Client decision recorded",
    "notify.plans.decidedBody": "{actor} recorded the client's decision on “{subject}”.",
    "notify.plans.withdrawnTitle": "Approval withdrawn",
    "notify.plans.withdrawnBody": "{actor} withdrew approval of “{subject}” and sent it back to production.",
    "notify.plans.cancelledTitle": "A plan item was cancelled",
    "notify.plans.cancelledBody": "{actor} cancelled “{subject}”.",
    "notify.plans.escalatedTitle": "A revision exception has been waiting",
    "notify.plans.escalatedBody": "“{subject}” has been parked on an exception for {hours, plural, one {# hour} other {# hours}} with no decision — the work is stopped on it.",
    "notify.plans.exceptionTitle": "Revision exception decided",
    "notify.plans.exceptionBody": "{actor} decided the exception on “{subject}”.",
    "notify.plans.mediaTitle": "New media on an item",
    "notify.plans.mediaBody": "{actor} added media to “{subject}”.",
    "notify.plans.commentTitle": "New comment",
    "notify.plans.commentBody": "{actor} commented on “{subject}”.",
    "notify.plans.mentionTitle": "{actor} mentioned you",
    "notify.plans.mentionBody": "In a comment on “{subject}”.",
    "notify.clients.publishingTitle": "Publishing responsibility changed",
    "notify.clients.publishingBody": "{actor} changed who publishes “{subject}”.",
    "notify.storage.orphanTitle": "Orphaned stored files",
    "notify.storage.orphanBody": "Reconciliation found {count, plural, one {# file} other {# files}}, including “{subject}” — storage and the database disagree. Needs a human decision.",
    "cards.attachments": "Attachments",
    "cards.attachNone": "No attachments.",
    "cards.attachAdd": "Add a file",
    "cards.detach": "Detach",
    "cards.detachedStillHeld": "Detached from this card — {n, plural, one {# other card still holds it} other {# other cards still hold it}}.",
    // ═══════════ Phase six — the portal ═══════════
    "errors.portalOperationRefused": "This operation is internal and not available from the portal.",
    "errors.portalDisabled": "This client's portal is not enabled.",
    "errors.pushNotConfigured": "Device notifications are not configured — contact the administrator.",
    "errors.previewStopped": "This employee's permissions allow this action. It did not run because it has a real effect.",
    "errors.previewPrivate": "Private chats do not open in this mode.",
    "preview.banner": "You are seeing the system as {name}. Nothing is saved. This ends at {until}.",
    "preview.exit": "Back to my account",
    "preview.start": "View as this employee",
    "preview.startHint": "See the system with their permissions; nothing is saved. It is logged under your name.",
    "plans.scope": "Show plans",
    "plans.scope.current": "Current",
    "plans.scope.archive": "Archive",
    "preview.wouldSucceed": "This would succeed for this employee. Nothing was written.",
    "preview.started": "You are now seeing the system as this employee.",
    "preview.stopped": "You are back in your own account.",
    "errors.workArchived": "This work is in the archive. It can be read but not changed.",
    "errors.tenantUnknown": "This address is not linked to any agency. Check your sign-in link.",
    "errors.backupNotConfigured": "The backup destination is not set — no backup is taken until it is.",
    "errors.backupFailed": "The backup could not be taken. The reason is in System health.",
    "notify.platform.backupFailedTitle": "The backup could not be taken",
    "notify.platform.backupFailedBody": "Open System health to read the reason. It retries every hour.",
    "platform.health.service.backup": "Backups",
    "platform.health.job.backup": "Backups",
    "platform.health.serviceHint.backup": "A copy of the database and files to a destination outside this server every six hours.",
    "errors.pushDeliveryFailed": "The test notification did not reach any device. Try again in a minute.",
    "portal.nav.home": "Home",
    "portal.nav.plans": "Plans",
    "portal.nav.notifications": "Notifications",
    "portal.nav.contacts": "Our team",
    "portal.nav.schedule": "Posts",
    "portal.nav.chat": "Chat",
    "portal.nav.meetings": "Meetings",
    "nav.meetings": "Meetings",
    "meetings.title": "Meetings",
    "meetings.subtitle": "Your client meetings — what awaits your answer comes first.",
    "meetings.state.REQUESTED": "Awaiting a reply",
    "meetings.state.RESCHEDULE_PROPOSED": "Another time proposed",
    "meetings.state.CONFIRMED": "Confirmed",
    "meetings.state.COMPLETED": "Held",
    "meetings.state.REJECTED": "Declined",
    "meetings.state.CANCELLED": "Cancelled",
    "meetings.state.NO_SHOW": "No-show",
    "meetings.side.AGENCY": "From us",
    "meetings.side.CLIENT": "From the client",
    "meetings.propose": "Request a meeting",
    "meetings.proposed": "The proposal was sent.",
    "meetings.subject": "What the meeting is about",
    "meetings.subjectRequired": "Write what the meeting is about.",
    "meetings.when": "When",
    "meetings.whenInPast": "The time must be in the future.",
    "meetings.duration": "Minutes",
    "meetings.link": "Meeting link",
    "meetings.linkHint": "Zoom or Meet — the client sees it only once confirmed.",
    "meetings.client": "Client",
    "meetings.confirm": "Confirm the time",
    "meetings.confirmed": "The meeting is confirmed.",
    "meetings.reschedule": "Propose another time",
    "meetings.rescheduled": "The proposal was sent.",
    "meetings.reject": "Decline",
    "meetings.rejected": "Declined.",
    "meetings.cancel": "Cancel the meeting",
    "meetings.cancelled": "Cancelled.",
    "meetings.complete": "It was held",
    "meetings.completed": "Recorded as held.",
    "meetings.noShow": "The other side did not show",
    "meetings.noShowSaved": "Recorded as a no-show.",
    "meetings.reason": "Reason",
    "meetings.reasonRequired": "Write the reason.",
    "meetings.proposals": "Proposals",
    "meetings.awaitingThem": "Waiting on the other side",
    "meetings.awaitingYou": "Waiting on you",
    "meetings.noneTitle": "No meetings",
    "meetings.noneBody": "When you or a client asks for a meeting, it appears here.",
    "meetings.linkHiddenUntilConfirmed": "The link appears once confirmed",
    "meetings.proposeButton": "+ Request a meeting",
    "meetings.awaitingYouCount": "Awaiting your answer · {count}",
    "meetings.requestedBy": "Requested by {name}",
    "meetings.minutes": "{count} min",
    "meetings.confirmShort": "Confirm",
    "meetings.decline": "Decline",
    "meetings.upcoming": "Upcoming",
    "meetings.upcomingNone": "No upcoming meetings.",
    "meetings.join": "Join the meeting",
    "meetings.options": "Options for “{subject}”",
    "meetings.changeTime": "Change the time",
    "meetings.pastTitle": "Past — did it happen?",
    "meetings.pastCount": "Past — did it happen? · {count}",
    "meetings.didNotHappen": "Didn't happen",
    "meetings.archive": "Archive",
    "meetings.archiveCount": "Archive · {count}",
    "meetings.archiveHint": "Cancelled, declined and closed meetings live here — out of the way.",
    "portal.meetings.subtitle": "Your times with your agency team.",
    "portal.meetings.request": "Request a meeting",
    "portal.meetings.requested": "Your request arrived.",
    "portal.meetings.noneTitle": "No meetings",
    "portal.meetings.noneBody": "Ask for a meeting and your team will reply with a time.",
    "portal.meetings.join": "Join the meeting",
    "portal.meetings.side.AGENCY": "Proposed by the agency",
    "portal.meetings.side.CLIENT": "Requested by your team",
    "portal.reports.editPending": "Edit requested — with the agency",
    "portal.schedule.subtitle": "When your work publishes, where each one stands, and the post's link once it is live.",
    "portal.schedule.state.PENDING": "Awaiting scheduling",
    "portal.schedule.state.SCHEDULED": "Scheduled",
    "portal.schedule.state.PUBLISHED": "Published",
    "portal.schedule.state.CANCELLED": "Cancelled",
    "portal.schedule.state.FAILED": "We are looking into this one",
    "portal.schedule.noDate": "No date set yet",
    "portal.schedule.openPost": "Open the post",
    "portal.takedown.ask": "Ask to take it down",
    "portal.takedown.reason": "Why should this post come down? — it reaches the publishing team at once",
    "portal.takedown.send": "Send the takedown request",
    "portal.takedown.open": "Takedown request received — the publishing team is on it",
    "portal.takedown.removed": "Taken down",
    "portal.takedown.refused": "Staying up",
    "portal.schedule.noneTitle": "Nothing scheduled",
    "portal.schedule.noneBody": "Once you approve work and its publishing dates are set, you will find them here with their state.",
    "portal.schedule.selfTitle": "Your team publishes your work",
    "portal.schedule.selfBody": "The agency doesn't publish for you, so no publishing slots show here. Work you approved is in Plans with its date.",
    "portal.reports.noneTitle": "No reports yet",
    "portal.reports.noneBody": "As soon as a report is sent to you, it appears here in full.",
    "portal.nav.reports": "Reports",
    "portal.nav.profile": "My profile",
    "portal.agency": "The agency",
    "portal.you": "You",
    "portal.home.awaitingNone": "Nothing is waiting for your decision.",
    "portal.home.soon": "Publishing soon",
    "portal.home.soonNone": "Nothing is publishing soon.",
    "portal.home.awaitingCount": "{n, plural, one {item awaits your decision} other {items await your decision}}",
    "portal.home.oldest": "The oldest reached you {since}",
    "portal.home.publishing": "Publishes {when}",
    "portal.home.meetingsWaiting": "{n, plural, one {# meeting awaits} other {# meetings await}} your reply",
    "portal.home.meetingsNone": "No meeting awaits your reply.",
    "portal.item.parts": "Parts",
    "portal.item.partApproved": "You approved it",
    "portal.item.revisionsAfter": "After the last revision, any change request goes to management before it's done.",
    "portal.item.approvedNote": "You approved it — it publishes on schedule",
    "portal.schedule.publishedSection": "Published · newest first",
    "portal.meetings.awaitingSection": "Awaiting your reply",
    "portal.meetings.upcomingSection": "Upcoming",
    "portal.meetings.pastSection": "Past",
    "portal.alerts": "Notifications — {n} new",
    "portal.plans.none": "No plans are available to you yet.",
    "portal.plans.noneBody": "As soon as the agency prepares and approves work, it appears here.",
    "portal.plans.itemsNone": "Nothing in this plan is available to you yet.",
    "portal.item.preview": "Preview",
    "portal.item.caption": "Post text",
    "portal.item.altCaption": "Alternative text",
    "portal.item.publishAt": "Publish at",
    "portal.item.history": "What happened",
    "portal.item.historyNone": "No decisions yet.",
    "portal.item.comments": "Comments",
    "portal.item.commentsNone": "No comments.",
    "portal.item.commentPlaceholder": "Write your comment…",
    "portal.item.approve": "Approve",
    "portal.item.reject": "Needs changes",
    "portal.item.requestEdit": "Request an edit",
    "portal.item.rejectComment": "Say exactly what is needed",
    "portal.item.partsLegend": "Which part needs changes?",
    "portal.item.rejectHint": "The comment is required — whoever edits needs to know what to change.",
    "portal.item.revisions": "Revisions",
    "portal.item.revisionsUsed": "You have used {used} of {limit}",
    "portal.item.exceptionPending": "Your request went to management — we will come back with a decision.",
    "portal.item.approved": "Approved, thank you.",
    "portal.item.rejected": "Your request reached the team.",
    "portal.item.parked": "Your request passed the agreed revision limit — it went to management.",
    "portal.item.editRequested": "The edit request has arrived.",
    "portal.item.commented": "Your comment was saved.",
    "portal.item.awaitingYou": "Awaiting your decision",
    "portal.decision.approvedByMe": "You approved",
    "portal.decision.rejectedByMe": "You requested changes",
    "portal.contacts.title": "Our team on the portal",
    "portal.contacts.none": "Nobody else from your team is on the portal.",
    "portal.contacts.primary": "Primary contact",
    "push.title": "Notifications on this device",
    "push.enable": "Notify me on this device",
    "push.disable": "Stop notifying this device",
    "push.enabled": "Notifications are on for this device.",
    "push.disabled": "Notifications are off for this device.",
    "push.blocked": "The browser is refusing notifications. Allow them in site settings and try again.",
    "push.unsupported": "This browser does not support device notifications.",
    "push.thisDevice": "This device",
    "push.devicesNoneTitle": "No devices enabled",
    "push.devicesNone": "Alerts are not enabled on any device yet. Enable them with the button above.",
    "push.removeDevice": "Stop alerts on this device",
    "push.stop": "Stop",
    "push.devices": "Enabled devices",
    "push.deviceRequired": "The device name is missing.",
    "push.endpointRequired": "The subscription details are incomplete — try again.",
    "push.keysRequired": "The subscription keys are missing — try again.",
    "push.intro": "You get a notification when work is assigned to you, someone mentions you, or a due date is near — even with the app closed. It carries a title and a link only, never the work itself.",
    "push.stateOn": "On",
    "push.stateOff": "Off",
    "push.stateBlocked": "Blocked by the browser",
    "push.test": "Send a test notification",
    "push.tested": "{n, plural, one {The test notification reached one device} other {The test notification reached # devices}}.",
    "push.testedNone": "No device is enabled yet — turn notifications on for this device first.",
    "push.testTitle": "Test notification from Minwal",
    "push.testBody": "Notifications reach this device.",
    "push.askTitle": "Get notifications on this device?",
    "push.askBody": "You get a notification when work is assigned to you, someone mentions you, or a due date is near — even with the app closed.",
    "push.askEnable": "Turn on notifications",
    "push.askLater": "Not now",
    "push.askNote": "You can change this any time from My account.",
    "notify.portal.decisionTitle": "The client decided",
    "notify.portal.decisionBody": "{actor} decided on «{subject}».",
    "notify.portal.editRequestTitle": "The client requested an edit",
    "notify.portal.editRequestBody": "{actor} requested an edit to «{subject}» after approving.",
    "notify.portal.commentTitle": "A comment from the client",
    "notify.portal.commentBody": "{actor} commented on «{subject}».",
    "notify.portal.awaitingTitle": "Work awaiting your decision",
    "notify.portal.awaitingBody": "«{subject}» is ready and awaiting your decision.",
    // ── تغييرُ المرء كلمتَه — وسياستُها في القلب ──
    "auth.passwordTooShort": "The password needs at least 12 characters.",
    "auth.currentPassword": "Current password",
    "auth.newPassword": "New password",
    "auth.confirmPassword": "Confirm the new password",
    "auth.currentPasswordRequired": "Enter your current password.",
    "auth.currentPasswordWrong": "That current password is wrong.",
    "auth.passwordMismatch": "The two passwords do not match.",
    "auth.passwordSameAsOld": "Choose a password different from the current one.",
    "auth.passwordChanged": "Your password has been changed.",
    "auth.changePasswordTitle": "Change your password",
    "auth.changePasswordWhy": "Your account was created with a temporary password. Change it before you continue.",
    // ── جملةٌ كاملة لا كلمتان تُلصَقان ──
    "portal.decision.agencyApproved": "The agency approved",
    "portal.decision.agencyRejected": "The agency requested changes",
    "portal.decision.personApproved": "{name} approved",
    "portal.decision.personRejected": "{name} requested changes",
    // ── الدعوة والدور — الطريقُ الوحيد لدخول إنسانٍ جديد ──
    "errors.inviteInvalid": "This invite link is not usable — ask an administrator to send a new one.",
    "invite.title": "Choose your password",
    "invite.sub": "This is your first sign-in. Pick a password and get started.",
    "invite.done": "Done — your password is set. Sign in with it now.",
    "invite.link": "Invite link",
    "invite.copy": "Copy the link",
    "invite.copied": "Link copied.",
    "invite.copyFailed": "The browser refused to copy — select the link and copy it yourself.",
    "invite.hint": "Send this link to the employee. It works once and expires after 7 days.",
    "invite.resend": "Create a new invite link",
    "invite.resendHint": "The old link will stop working.",
    "invite.state.none": "No invite",
    "invite.state.pending": "Invite pending",
    "invite.state.expired": "Invite expired",
    "invite.state.used": "Already signed in",
    "people.inviteSection": "Sign-in & security",
    "people.role": "Role",
    "people.roleNone": "No role",
    "people.changeRole": "Change role",
    "people.roleChanged": "Role changed.",
    "people.roleRequired": "Pick a role — an employee without one can do nothing.",
    "people.counts": "{total, plural, one {# person} other {# people}} · {active} active · {pending} awaiting invite · {stopped} stopped",
    "people.search": "Search by name or email…",
    "people.searchLabel": "Search",
    "people.pendingFilter": "Awaiting invite",
    "people.invitePending": "Hasn't accepted the invite",
    "people.noDepartment": "No department",
    "people.noMatch": "Nobody by that name here.",
    "people.lastSeen": "Last signed in {when}",
    "people.neverSeen": "Never signed in",
    "people.roleAndDepartments": "Role & departments",
    "people.primary": "primary",
    "people.overrideMetaGrant": "Until {when} · Reason: {reason} · Granted by {name}",
    "people.overrideMetaRevoke": "Until {when} · Reason: {reason} · Revoked by {name}",
    "people.deleteOpen": "Delete permanently…",
    // ── بورتال العميل: فتحُه وجهاتُ اتصاله ──
    "notify.clients.portalTitle": "The client's portal changed",
    "notify.clients.portalBody": "{actor} changed the portal state of «{subject}».",
    "clients.portalSaved": "The portal state was saved.",
    "clients.contactRemoved": "The contact was removed.",
    "clients.portalSection": "Client portal",
    "clients.portalOn": "Portal is open",
    "clients.portalOff": "Portal is closed",
    "clients.portalOnHint": "The client sees approved work and can approve or reject it.",
    "clients.portalOffHint": "Contacts exist but reach nothing until you open it.",
    "clients.contacts": "Contacts",
    "clients.contactsNone": "No contacts yet. Add one so the client can sign in.",
    "clients.addContact": "Add a contact",
    "clients.contactPrimary": "Make primary",
    "clients.contactRemove": "Remove from the portal",
    "clients.contactRemoveConfirm": "Remove «{name}» from this client's portal?",
    "clients.contactOtherClients": "Also with {n, plural, one {# other client} other {# other clients}}",
    "clients.contactRole": "Portal role",
    "clients.contactRoleNone": "No portal role exists yet — create one from the Roles screen first.",
    "invite.hintContact": "Send this link to the contact. It works once and expires in 7 days.",
    "clients.contactIsPrimary": "Primary",
    "clients.contactKeepsAccount": "Their account stays active for the other clients they serve.",
    "clients.contactLosesAccount": "This is their last client — the account is suspended and the session ends. Adding them back restores it.",
    "clients.contactsEmptyTitle": "No portal contacts",
    "clients.contactRoleEmpty": "No portal role",
    "clients.contactName": "Contact name",
    "clients.contactChooseRole": "Choose the role",
    "errors.releaseStateInvalid": "The release is {state} — that action does not apply.",
    "errors.releaseNotYoursToPublish": "You cannot publish a release you wrote — a colleague reads and publishes it.",
    "errors.notificationParamMissing": "The new text is missing required placeholders: {missing}.",
    "platform.purgeReasonRequired": "Write why you are purging.",
    "platform.releaseFieldRequired": "This field is required — in both languages.",
    "platform.audit.title": "Activity log",
    "platform.audit.subtitle": "Who did what and when — and every line opens the thing itself.",
    "platform.audit.system": "The system",
    "platform.audit.changes": "What changed",
    "platform.audit.from": "Was",
    "platform.audit.to": "Now",
    "platform.audit.correlation": "Reference",
    "platform.audit.severity": "Level",
    "platform.audit.filterFrom": "From",
    "platform.audit.filterTo": "To",
    "platform.audit.noneTitle": "No activity in this period",
    "platform.audit.noneBody": "Change the period or the person and try again.",
    "platform.audit.purge": "Purge the old",
    "platform.audit.purgeHint": "Deletes anything older than the window set in settings. The purge itself is logged.",
    "platform.audit.purged": "{deleted, plural, one {# row} other {# rows}} purged.",
    "platform.health.service.database": "Database",
    "platform.health.service.storage": "Storage",
    "platform.health.service.scanner": "File scanner",
    "platform.health.service.mail": "Email",
    "platform.health.service.push": "Push alerts",
    "platform.health.state.up": "Up",
    "platform.health.state.down": "Down",
    "platform.health.state.off": "Off",
    "platform.health.queues": "Queues",
    "platform.health.outboxPending": "Awaiting delivery",
    "platform.health.outboxUndelivered": "Over a minute late",
    "platform.health.scansPending": "Files being scanned",
    "platform.health.incidentsOpen": "Open incidents",
    "platform.health.jobs": "Work that runs on its own",
    "platform.health.lastEvent": "Last produced something",
    "platform.health.lastRun": "Last run",
    "platform.health.notRunYet": "Not run yet",
    "platform.health.runFailed": "Failed {ago}",
    "platform.health.never": "Never",
    "platform.health.incidents": "Incidents",
    "platform.health.resolve": "Resolved",
    "platform.health.resolved": "The incident is closed.",
    "platform.health.occurrences": "Seen {occurrences, plural, one {once} other {# times}}",
    "platform.health.noIncidentsTitle": "No open incidents",
    "platform.health.noIncidentsBody": "Any failure opens one here with its real cause and reference.",
    "platform.changelog.title": "Changelog",
    "notify.platform.release.title": "A new version is out",
    "notify.platform.release.body": "Version {version} has landed — open it to see what is new.",
    "platform.changelog.subtitle": "What is new in each release — written as a draft, reviewed, then shown to everyone in “What's new”.",
    "platform.changelog.new": "+ New release",
    "platform.changelog.version": "Version",
    "platform.changelog.titleAr": "Title in Arabic",
    "platform.changelog.titleEn": "Title in English",
    "platform.changelog.bodyAr": "Details in Arabic",
    "platform.changelog.bodyEn": "Details in English",
    "platform.changelog.state.DRAFT": "Draft",
    "platform.changelog.state.IN_REVIEW": "In review",
    "platform.changelog.state.PUBLISHED": "Published",
    "platform.changelog.sendToReview": "Send for review →",
    "platform.changelog.sentToReview": "Sent for review.",
    "platform.changelog.publish": "Publish to everyone →",
    "platform.changelog.published": "Published.",
    "platform.changelog.saved": "Saved.",
    "platform.changelog.noneTitle": "No releases",
    "platform.changelog.noneBody": "Write the first one and tell the team what is new.",
    "platform.changelog.whatsNew": "What is new",
    "platform.changelog.gotIt": "Got it",
    "platform.changelog.by": "Written by {name}",
    "platform.changelog.reviewedBy": "Reviewed by {name}",
    "platform.changelog.publishedAt": "Published {when}",
    "platform.notifications.title": "Notification wording",
    "platform.notifications.subtitle": "Reword any notification in both languages.",
    "platform.notifications.default": "Default",
    "platform.notifications.custom": "Edited",
    "platform.notifications.push": "Reaches the phone",
    "platform.notifications.email": "Reaches email",
    "platform.notifications.audienceFixed": "Who receives it comes from the capabilities — not from here.",
    "platform.notifications.reset": "Back to the original",
    "platform.notifications.saved": "The wording is saved.",
    "platform.notifications.wasReset": "Back to the default.",
    "platform.notifications.noneTitle": "No notifications",
    "platform.notifications.noneBody": "The notification registry is empty — and it never is.",
    "platform.audit.severityWarning": "Important",
    "platform.audit.severityCritical": "Alert",
    "platform.audit.allEntities": "Everything",
    "platform.audit.allSeverities": "All levels",
    "platform.audit.clearFilters": "Clear filters",
    "platform.audit.filterCorrelation": "Show everything done under this number",
    "platform.audit.purgeReason": "Why are you purging",
    "platform.audit.who": "Who",
    "platform.audit.allPeople": "Everyone",
    "platform.audit.fromClient": "{name} (client)",
    "platform.audit.onWhat": "On what",
    "platform.audit.wholeGroup": "All of {group}",
    "platform.audit.period": "Period",
    "platform.audit.periodAll": "All time",
    "platform.audit.periodDay": "Last 24 hours",
    "platform.audit.periodWeek": "Last 7 days",
    "platform.audit.periodMonth": "Last 30 days",
    "platform.audit.periodCustom": "Pick a range",
    "platform.audit.count": "{total, plural, one {# event} other {# events}}",
    "platform.audit.details": "Details",
    "platform.audit.hideDetails": "Close",
    "platform.audit.fieldHead": "What changed",
    "platform.audit.correlationHint": "If anyone reports an error, this number leads you to this line.",
    "platform.audit.value.active": "Active",
    "platform.audit.field.afterColumn": "After column",
    "platform.audit.field.approved": "Approved",
    "platform.audit.field.archived": "Archived",
    "platform.audit.field.assignees": "Assignees",
    "platform.audit.field.attendees": "Attendees",
    "platform.audit.field.before": "Deleted before",
    "platform.audit.field.capabilities": "Capabilities",
    "platform.audit.field.capability": "Capability",
    "platform.audit.field.code": "Code",
    "platform.audit.field.column": "Column",
    "platform.audit.field.columns": "Columns",
    "platform.audit.field.cycle": "Cycle",
    "platform.audit.field.deactivated": "Deactivated",
    "platform.audit.field.delegate": "Delegate",
    "platform.audit.field.deleted": "Deleted",
    "platform.audit.field.departments": "Departments",
    "platform.audit.field.description": "Description",
    "platform.audit.field.detachedFrom": "Detached from",
    "platform.audit.field.dueAt": "Due",
    "platform.audit.field.email": "Email",
    "platform.audit.field.event": "Notification",
    "platform.audit.field.export": "Export",
    "platform.audit.field.filename": "File name",
    "platform.audit.field.fromColumn": "From column",
    "platform.audit.field.gigabytes": "Space (GB)",
    "platform.audit.field.granted": "Granted",
    "platform.audit.field.head": "Head of department",
    "platform.audit.field.itemState": "Item state",
    "platform.audit.field.key": "Entry",
    "platform.audit.field.kind": "Kind",
    "platform.audit.field.livePublished": "Actually published",
    "platform.audit.field.locale": "Language",
    "platform.audit.field.metric": "Metric",
    "platform.audit.field.months": "Period (months)",
    "platform.audit.field.movedTo": "Moved to",
    "platform.audit.field.name": "Name",
    "platform.audit.field.nameAr": "Arabic name",
    "platform.audit.field.nameEn": "English name",
    "platform.audit.field.otherSessionsRevoked": "Other devices signed out",
    "platform.audit.field.owner": "Owner",
    "platform.audit.field.parked": "Parked",
    "platform.audit.field.period": "Period",
    "platform.audit.field.portalEnabled": "Portal open",
    "platform.audit.field.priority": "Priority",
    "platform.audit.field.proposedDueAt": "Proposed due",
    "platform.audit.field.publishedByAgency": "Published by the agency",
    "platform.audit.field.reason": "Reason",
    "platform.audit.field.recurring": "Recurring",
    "platform.audit.field.remindersScheduled": "Reminders scheduled",
    "platform.audit.field.revokedApproval": "Approval withdrawn",
    "platform.audit.field.role": "Role",
    "platform.audit.field.sessionsEnded": "Devices signed out",
    "platform.audit.field.sizeBytes": "Size (bytes)",
    "platform.audit.field.state": "State",
    "platform.audit.field.status": "Account state",
    "platform.audit.field.steps": "Steps",
    "platform.audit.field.stillHeld": "Still used elsewhere",
    "platform.audit.field.subject": "Subject",
    "platform.audit.field.tasksMoved": "Tasks moved",
    "platform.audit.field.theme": "Theme",
    "platform.audit.field.timezone": "Timezone",
    "platform.audit.field.title": "Title",
    "platform.audit.field.toColumn": "To column",
    "platform.audit.field.tone": "Colour",
    "platform.audit.field.person": "Person",
    "platform.audit.field.value": "Value",
    "platform.audit.field.version": "Version",
    "platform.audit.field.visible": "Visible to the client",
    "platform.health.why": "Why",
    "platform.health.took": "{millis} ms",
    "platform.health.every": "Every {every}",
    "platform.health.showOpen": "Open",
    "platform.health.showResolved": "Resolved",
    "platform.health.firstSeen": "First seen",
    "platform.health.lastSeen": "Last seen",
    "platform.health.operation": "Operation",
    "platform.health.noResolvedTitle": "No resolved incidents",
    "platform.health.noResolvedBody": "Every incident you resolve shows here with the time it was closed.",
    "platform.health.allGood": "Every service is up — nothing is waiting on anyone",
    "platform.health.someIncidents": "Every service is up — {n, plural, one {# incident is} other {# incidents are}} waiting for someone",
    "platform.health.serviceDown": "A service is down: {names}",
    "platform.health.checkedAgo": "Last checked {ago} · refreshes on its own every 30 seconds",
    "platform.health.checkNow": "Check now",
    "platform.health.services": "Services",
    "platform.health.serviceHint.database": "All of the system's data",
    "platform.health.serviceHint.storage": "Images, videos and files",
    "platform.health.serviceHint.scanner": "Scans every uploaded file",
    "platform.health.serviceHint.mail": "Invitations and password changes",
    "platform.health.serviceHint.push": "What reaches the phone",
    "platform.health.jobName": "What",
    "platform.health.jobEvery": "Runs",
    "platform.health.silent": "Sends no notifications, by design",
    "platform.health.job.cardRelay": "Card reminders and escalation",
    "platform.health.job.recurrenceRelay": "Recurring cards",
    "platform.health.job.exporter": "Client data exports",
    "platform.health.job.exceptionEscalation": "Revision exception escalation",
    "platform.health.job.goalRelay": "Goal tracking",
    "platform.health.job.foldRelay": "Folding quiet channels",
    "platform.health.job.meetingRelay": "Meeting reminders",
    "platform.health.job.reconciler": "Storage review",
    "platform.health.job.dispatcher": "Notification delivery",
    "platform.health.job.attachmentScanner": "Scanning uploaded files",
    "platform.notifications.searchHint": "“due”, “client”…",
    "platform.notifications.english": "In English",
    "platform.notifications.insert": "Insert:",
    "platform.notifications.insertParam": "+ {name}",
    "platform.notifications.inboxAlways": "The in-app inbox always receives it — it cannot be switched off.",
    "platform.notifications.preview": "It will arrive like this",
    "platform.notifications.previewNow": "now",
    "platform.notifications.previewInbox": "In the notification inbox",
    "platform.notifications.previewNote": "The preview uses sample names, so you see the whole sentence before saving.",
    "platform.notifications.firedBy": "Sent by: {what}",
    "platform.notifications.cadence": "Sent automatically {every}",
    "platform.notifications.pickOne": "Pick a notification from the list to edit its wording.",
    "platform.notifications.param.count": "Count",
    "platform.notifications.param.subject": "Item name",
    "platform.notifications.param.actor": "Who did it",
    "platform.notifications.param.block": "Why it is blocked",
    "platform.notifications.param.channel": "Channel name",
    "platform.notifications.param.client": "Client name",
    "platform.notifications.param.code": "Code",
    "platform.notifications.param.days": "Number of days",
    "platform.notifications.param.decision": "Decision",
    "platform.notifications.param.due": "Due time",
    "platform.notifications.param.excerpt": "Start of the message",
    "platform.notifications.param.hours": "Number of hours",
    "platform.notifications.param.metric": "Metric",
    "platform.notifications.param.missed": "What was missed",
    "platform.notifications.param.outcome": "Outcome",
    "platform.notifications.param.percent": "Percentage",
    "platform.notifications.param.platform": "Platform",
    "platform.notifications.param.reason": "Reason",
    "platform.notifications.param.resolution": "Resolution",
    "platform.notifications.param.target": "Target",
    "platform.notifications.param.task": "Task name",
    "platform.notifications.param.title": "Title",
    "platform.notifications.param.value": "Value",
    "platform.notifications.param.version": "Version",
    "platform.notifications.param.weeks": "Number of weeks",
    "platform.notifications.param.when": "Time",
    "platform.notifications.sample.count": "3",
    "platform.notifications.sample.subject": "Landing page copy",
    "platform.notifications.sample.actor": "Shady Sirag",
    "platform.notifications.sample.block": "Waiting for the client's photos",
    "platform.notifications.sample.channel": "Design team",
    "platform.notifications.sample.client": "Mint Café",
    "platform.notifications.sample.code": "4821",
    "platform.notifications.sample.days": "3",
    "platform.notifications.sample.decision": "The exception was approved",
    "platform.notifications.sample.due": "Thursday 5 pm",
    "platform.notifications.sample.excerpt": "Can we change the background colour?",
    "platform.notifications.sample.hours": "24",
    "platform.notifications.sample.metric": "On-time delivery",
    "platform.notifications.sample.missed": "2",
    "platform.notifications.sample.outcome": "Approved",
    "platform.notifications.sample.percent": "80",
    "platform.notifications.sample.platform": "Instagram",
    "platform.notifications.sample.reason": "The photo needs more light",
    "platform.notifications.sample.resolution": "The post was taken down",
    "platform.notifications.sample.target": "90%",
    "platform.notifications.sample.task": "Cover — REEL 4",
    "platform.notifications.sample.title": "October report",
    "platform.notifications.sample.value": "85%",
    "platform.notifications.sample.version": "3.2",
    "platform.notifications.sample.weeks": "2",
    "platform.notifications.sample.when": "tomorrow 11 am",
    "settings.sections": "Settings sections",
    "settings.groupHint.identity": "Name, colour and language",
    "settings.groupHint.work": "Due times and escalation",
    "settings.groupHint.plans": "Client revision limit and exceptions",
    "settings.groupHint.storage": "Space and file limits",
    "settings.groupHint.security": "Sign-in and two-step checks",
    "settings.groupHint.reports": "Report timing",
    "settings.groupHint.retention": "Retention and backups",
    "settings.unsaved": "{n} changed — not saved",
    "settings.revert": "Revert",
    "settings.save": "Save",
    "settings.addReminder": "+ Reminder",
    "settings.reminderBefore": "{hours} h before",
    "settings.removeReminder": "Remove the {hours} h reminder",
    "settings.reminderHours": "How many hours before the due time?",
    "settings.addReminderConfirm": "Add",
    "platform.notifications.titleAr": "Title in Arabic",
    "platform.notifications.titleEn": "Title in English",
    "platform.notifications.bodyAr": "The text in Arabic",
    "platform.notifications.bodyEn": "The text in English",
    "platform.notifications.search": "Search the wording",
    "actions.title": "On you",
    "actions.subtitle": "Every decision waiting on you, in one place. An item clears once you do it.",
    "actions.noneTitle": "Nothing is waiting on you",
    "actions.noneBody": "The moment a decision lands on you it shows here, with a button that opens it.",
    "actions.kind.cardDueChange": "A due-date change is waiting on you",
    "actions.kind.accessRequest": "An access request is waiting on you",
    "actions.kind.reportHead": "A report is waiting for your head review",
    "actions.kind.reportAdmin": "A report is waiting for admin publishing",
    "actions.kind.reportException": "An exception request is waiting on you",
    "actions.kind.revisionException": "An item is held on a revision exception waiting on you",
    "actions.kind.takedown": "A takedown request is waiting on you",
    "actions.kind.meeting": "A meeting is waiting for your answer",
    "actions.kind.portalItem": "An item is waiting for your approval",
    "actions.kind.portalMeeting": "A meeting is waiting for your answer",
    "actions.open": "Open it",
    "actions.onlyKind": "Showing only: {kind}",
    "actions.onlyKindNone": "Nothing of this kind is waiting on you right now.",
    "actions.showAll": "Show all",
    "actions.defer": "Remind me later",
    "actions.deferTomorrow": "Tomorrow",
    "actions.deferWeek": "In a week",
    "actions.deferUntil": "Remind me on",
    "actions.deferred": "Snoozed until {until}.",
    "actions.deferredCount": "Deferred · {count} — they come back on their own",
    "actions.wakeNow": "Wake it now",
    "actions.woke": "It is back on the list.",
    "actions.since": "Waiting since",
    "actions.deferUntilInvalid": "Pick a date after today and within a month.",
    "actions.deferPick": "Pick a time…",
    "actions.deferredTitle": "Deferred",
    "actions.returnsAt": "Back {when}",
    "portal.nav.actions": "On you",
    /**
     * **عائلةُ الخيط — مفاتيحُ مكوّنٍ واحدٍ لا مفاتيحُ شاشة.**
     *
     * **وكانت `cards.comment*`** لأن الخيطَ كان في لوحة الكارت وحدها. ولمّا نزل
     * على عنصر الخطة كان الطريقُ إمّا مفاتيحَ ثانيةً بنفس النصّ — **فيفترق
     * النصّان عند أوّل تعديل** — أو عائلةً واحدةً للمكوّن. **فالمفاتيحُ تتبع
     * المكوّنَ لا موضعَه.**
     */
    "thread.title": "Comments",
    "thread.empty": "No comments.",
    "thread.placeholder": "Write a comment…",
    "thread.send": "Send",
    "thread.editedTag": "edited",
    "thread.required": "Write the comment.",
    "thread.clientVisible": "In the client's thread",
    "thread.replyInChat": "This thread is internal — the client cannot see your reply here. Answer them in chat.",
    /**
     * **سطوحٌ نزلت بقرارات شادي 2026-10-20** — الملفاتُ على التعليقات،
     * وملفاتُ التقرير، وحضورُ الاجتماع.
     */
    "meetings.attendees": "Attendees",
    "meetings.attendeesSaved": "The attendee list was saved.",
    "meetings.attendeesNone": "Nobody set yet.",
    "meetings.attendeesPick": "Pick who attends",
    "reports.files": "Report files",
    "reports.filesNone": "No files attached to the report.",
    "thread.attach": "Attach a file",
    "notify.reports.commented.title": "A comment on a report",
    "notify.reports.commented.body": "A new comment on the report â{title}â.",
    "notify.meetings.commented.title": "A comment on a meeting",
    "notify.meetings.commented.body": "A new comment on the meeting â{subject}â.",
    "notify.meetings.attendee.title": "You are attending a meeting",
    "notify.meetings.attendee.body": "You were added to the meeting â{subject}â.",
    /**
     * **ونوعٌ ثالثٌ لليتيم** (2026-10-24): مفتاحٌ في القاعدة **خارج كلِّ درجٍ
     * تسرح فيه المصالحة** — فلا يُقال ناقصاً ولا سليماً، يُقال إنه خارج ما نعرف.
     */
    "storage.orphans.unknownSpace": "A key outside every known space",
    "storage.orphans.unknownKind": "Unknown kind",
    "storage.title": "Storage",
    "storage.subtitle": "Each client's space, and the files that belong to no one.",
    "storage.usedTotal": "Used in total",
    "storage.nearest": "Client closest to its limit",
    "storage.nearestNone": "No clients",
    "storage.nearestValue": "{name} {percent}%",
    "storage.nearestCalm": "None — highest is {name} {percent}%",
    "storage.orphans.tile": "Orphaned files",
    "storage.orphans.tileValue": "{n} · {size}",
};
//# sourceMappingURL=en.js.map